import { createBabylonRuntime } from "@/core/engine/createBabylonRuntime";
import { createHoverIdleScene } from "@/core/scene/createHoverIdleScene";
import { createHoverIdleAppSession } from "@/app/createHoverIdleAppSession";
import {
  applyE2EInitialStatePatch,
  readE2EInitialStatePatch,
} from "@/app/e2e/e2eInitialState";
import { markE2EForegroundPresentation } from "@/app/e2e/e2eForegroundPresentation";
import {
  createCustomContentScreenIds,
  createGameNavItems,
  isGameNavItemVisible,
  resolveAvailableGameScreenId,
  type GameNavigationAvailability,
} from "@/app/gameNavigation";
import { formatDuration, getMessages, setCurrentLocale } from "@/i18n/runtime";
import { resolvePlayerOwner } from "@/app/game/playerOwner";
import { createDefaultSaveStorage } from "@/app/game/saveStorage";
import { resolveInitialGameAppAchievementPersistentEffects } from "@/app/game/initialAchievementPersistentEffects";
import {
  createPaymentRepositories,
  mergeBundleEntitlements,
} from "@/app/game/paymentRepositories";
import { createRecentHovRateTracker } from "@/app/game/recentHovRateTracker";
import { createRecentHovCoreRateClock } from "@/app/game/recentHovCoreRateClock";
import { createRecentHovCoreRateTracker } from "@/app/game/recentHovCoreRateTracker";
import { dispatchWallClearNotifications } from "@/app/game/wallClearNotifications";
import { createWallActionHandlers } from "@/app/game/wallActionHandlers";
import {
  commitGameplayAutomationBurstSideEffects,
  type GameplayAutomationBurstPlayLogItem,
} from "@/app/game/gameplayAutomationBurst";
import { createAutoResetTargetPresentationGate } from "@/app/game/autoResetTargetPresentationGate";
import {
  createHovSurfaceReadoutSnapshotMemory,
  type AutoResetReadoutLayer,
} from "@/app/game/hovSurfaceReadoutSnapshot";
import { createPostPaintHovTargetHoverRefreshScheduler } from "@/app/game/hovTargetHoverRefreshAfterReset";
import { createForegroundFrameDriver } from "@/app/game/foregroundFrameDriver";
import { createStandbyNavigationToggle } from "@/app/game/standbyNavigationToggle";
import { createStandbyView } from "@/app/game/standbyView";
import {
  createHovGainUpgradePurchaseSideEffectBatcher,
  type HovGainUpgradeMilestonePurchaseSideEffectEvent,
} from "@/app/hovGainUpgradePurchaseSideEffects";
import { createScreenCollection } from "@/app/game/screenCollection";
import {
  createNavigationElements,
  syncNavigationActiveState,
} from "@/app/game/navigationView";
import {
  createOfflineRewardResultModel,
  formatOfflineRewardToast,
  formatRejectedOfflineRewardSummary,
  formatUnavailableOfflineRewardSummary,
} from "@/app/game/offlineRewardFormatter";
import { createOfflineRewardPresentationDispatcher } from "@/app/offlineRewardPresentation";
import {
  createOfflineRewardResultModal,
  type OfflineRewardResultModal,
  type OfflineRewardResultModalModel,
} from "@/features/hoverIdle/offline/OfflineRewardResultModal";
import { AchievementsModel } from "@/features/achievements/achievementsModel";
import {
  ACHIEVEMENT_NOTIFICATION_MAX_VISIBLE_COUNT,
  ACHIEVEMENT_NOTIFICATION_TOAST_GROUP,
} from "@/features/achievements/achievementNotificationLimits";
import {
  computePersistentEffects,
  getOneTimeEffect,
  type AchievementPersistentEffects,
} from "@/features/achievements/achievementEffects";
import { createGameUpgradeRepeatInitialIntervals } from "@/app/game/upgradeRepeatInitialIntervals";
import {
  ExplorationView,
  type ExplorationScreenSnapshot,
} from "@/features/hoverIdle/exploration/explorationView";
import type { ExplorationModelCommandResult } from "@/features/hoverIdle/exploration/explorationTypes";
import { AchievementsView } from "@/features/achievements/achievementsView";
import { RecordsModel } from "@/features/records/recordsModel";
import { RecordsView } from "@/features/records/recordsView";
import { computeStatsSnapshot } from "@/features/records/statsCalculator";
import type { RecordsState } from "@/features/records/types";
import type { AdminPanel } from "@/features/admin/adminPanel";
import type { AdminCheckpointController } from "@/features/admin/checkpoints/adminCheckpointController";
import type { BalanceLabPanel } from "@/features/balanceLab/balanceLabPanel";
import { purgeOwnUserData } from "@/features/admin/adminPurgeClient";
import {
  getAppEnvironment,
  isAdminPanelAvailable,
  isAdminPurgeAvailable,
  isBalanceLabAvailable,
} from "@/app/appEnvironment";
import type { HoverIdleAdminStateBundle } from "@/features/admin/adminState";
import { applyAdminStateBundleToModels } from "@/app/game/adminStateBundleApplication";
import { createLocalStorageAdminRuntimePreferenceStorage } from "@/features/admin/adminRuntimePreferences";
import {
  createAdminGameSpeedPolicyController,
  normalizeAdminGameSpeedMultiplier,
  type AdminGameSpeedElapsedPolicy,
} from "@/features/admin/adminGameSpeedPolicy";
import { createLocalStorageAdminProgressBuilderSeedStorage } from "@/features/admin/progressBuilder/adminProgressBuilderSeedStorage";
import {
  resolveAdminHoverToggleDurationMs,
  type AdminHoverToggleDelayMsOverride,
} from "@/features/admin/adminHoverToggleDelayOverride";
import type {
  AchievementId,
  AchievementsState,
} from "@/features/achievements/types";
import {
  createSettingsPanel,
  type SettingsPanel,
} from "@/features/settings/settingsPanel";
import {
  createDataPanel,
  type DataPanel,
  type DataPanelLoadResult,
  type DataPanelEraseProgressResult,
} from "@/features/data/dataPanel";
import {
  createPaymentPanel,
  type PaymentPanel,
} from "@/features/payment/paymentPanel";
import { syncEntitlementsAndApplyEffects } from "@/features/payment/entitlementEffects";
import { loadEntitlementSnapshot } from "@/features/payment/entitlementService";
import { purchaseShopItem } from "@/features/payment/shopPurchaseService";
import { summarizePermanentPurchaseEffects } from "@/features/payment/paymentPermanentEffects";
import {
  DEFAULT_ENTITLEMENTS,
  type BundleEntitlementKey,
  type PermanentPurchaseId,
  type PlayerOwner,
  type TokenPackId,
} from "@/features/payment/types";
import { TimView } from "@/features/hoverIdle/tim/timView";
import { HoverIdleModel } from "@/features/hoverIdle/core/hoverIdleModel";
import {
  applyAccountPurgeToSettings,
  applyProgressEraseToSettings,
} from "@/features/hoverIdle/core/hoverIdleSettingsReset";
import { resolveHoverIdleMotionPolicy } from "@/features/hoverIdle/core/hoverIdleMotionPolicy";
import { OutsideResetBurstController } from "@/features/hoverIdle/outsideReset/outsideResetBurstController";
import { OutsideResetBurstScheduler } from "@/features/hoverIdle/outsideReset/outsideResetBurstScheduler";
import { FirstAccessTutorialOverlay } from "@/features/hoverIdle/tutorial/firstAccessTutorialOverlay";
import { HovView, type CoreLoopTabId } from "@/features/hoverIdle/hov/hovView";
import {
  FAIRY_POP_FAI_REWARD,
  FairyModel,
} from "@/features/hoverIdle/fairy/fairyModel";
import { FairyView } from "@/features/hoverIdle/fairy/fairyView";
import {
  HovSkillModel,
  type HovSkillState,
} from "@/features/hoverIdle/hov/hovSkillModel";
import type {
  HovSkillCost,
  HovSkillHoldMs,
  HovSkillResourceBalances,
} from "@/features/hoverIdle/hov/hovSkillDefinitions";
import {
  resolveHovSkillEffects,
  type HovSkillEffectModifier,
} from "@/features/hoverIdle/hov/hovSkillEffects";
import {
  collectHovSkillEffectModifiers,
  type HovSkillEffectModifierProvider,
} from "@/features/hoverIdle/hov/hovSkillModifierProviders";
import {
  TimSkillModel,
  type TimSkillState,
} from "@/features/hoverIdle/tim/timSkillModel";
import { MpmView } from "@/features/hoverIdle/mpm/mpmView";
import { WallView } from "@/features/hoverIdle/wall/wallView";
import {
  WALL_START_CHARGE_MS_OPTIONS,
  type WallStartChargeMs,
} from "@/features/hoverIdle/wall/wallStartCharge";
import {
  createMpmAmplifierUpgradeAutomationController,
  type MpmAmplifierUpgradeAutomationController,
} from "@/features/hoverIdle/mpm/mpmAmplifierUpgradeAutomation";
import type { TimSkillId } from "@/features/hoverIdle/tim/timSkillDefinitions";
import { setCurrentNotation } from "@/shared/format/numberFormat";
import { waitForPaint } from "@/shared/ui/waitForPaint";
import { createHoverIdleAutosaveController } from "@/features/hoverIdle/persistence/hoverIdleAutosave";
import { createPlayLogRuntime } from "@/features/hoverIdle/playLog/playLogRuntime";
import { buildPlayLogHovSkillSummary } from "@/features/hoverIdle/playLog/playLogBalanceSummary";
import type { PlayLogStatus } from "@/features/hoverIdle/playLog/playLogTypes";
import {
  buildHoverIdleBackupFilename,
  decodeHoverIdleBackupText,
  encodeHoverIdleBackupText,
} from "@/features/hoverIdle/persistence/hoverIdleBackupFile";
import {
  createEmptyHovSkillState,
  createEmptyTimSkillState,
  createHoverIdleSaveData,
  achievementsStateFromHoverIdleSaveData,
  emptyAchievementsState,
  hovSkillStateFromHoverIdleSaveData,
  modelStateFromHoverIdleSaveData,
  recordsStateFromHoverIdleSaveData,
  timSkillStateFromHoverIdleSaveData,
  type HoverIdleSaveData,
} from "@/features/hoverIdle/persistence/hoverIdleSaveData";
import type { HoverIdleSaveStorage } from "@/features/hoverIdle/persistence/hoverIdleStorage";
import {
  advanceOfflineRewardRevision,
  cloneOfflineRewardSaveMetadata,
  createDefaultOfflineRewardSaveMetadata,
} from "@/features/hoverIdle/persistence/offlineRewardSaveMetadata";
import type {
  HoverIdleModelState,
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  HovGainUpgradeBulkPurchaseResult,
  HovGainUpgradeMpTarget,
} from "@/features/hoverIdle/types";
import { HoverPointerModel } from "@/features/hoverIdle/input/hoverPointerModel";
import { HoverPointerInputController } from "@/features/hoverIdle/input/hoverPointerInputController";
import { HoverPointerView } from "@/features/hoverIdle/input/hoverPointerView";
import {
  createHoverAnchorPersistenceController,
  createLocalStorageHoverAnchorStorage,
} from "@/features/hoverIdle/input/hoverAnchorStorage";
import {
  createHoverAnchorRestoreController,
  type HoverAnchorRestoreReason,
} from "@/features/hoverIdle/input/hoverAnchorRestoreController";
import type { Disposable } from "@/shared/contracts/lifecycle";
import { createAuthState } from "@/auth/authState";
import { createLinkAccountService } from "@/auth/linkAccountService";
import {
  createSettingsAccountSection,
  type SettingsAccountSection,
} from "@/features/settings/settingsAccountSection";
import { HelpView } from "@/features/help/helpView";
import {
  hasUnreadHelpTopic,
  isHelpTopicUnread,
  type HelpTopicId,
} from "@/features/help/helpTopics";
import {
  saveBackupText,
  type SaveBackupTextResult,
} from "@/app/saveBackupText";
import { huge, hugeZero, type HugeNumber } from "@/shared/numbers";
import { bootMark, bootMeasureAsync } from "@/app/bootTimingLogger";
import { isSupabaseConfigured } from "@/integrations/supabase/supabaseConfig";
import { markStartupTrace, measureStartupReward } from "@/app/startupTrace";
import {
  createAbsenceLifecycleCoordinator,
  resolveSamePageOfflineBaselineAt,
} from "@/app/absenceLifecycleCoordinator";
import {
  calculateAndCommitProductionOfflineRewardSave,
  type ProductionOfflineRewardOutcome,
} from "@/features/hoverIdle/offline/productionOfflineReward";
import { OFFLINE_REWARD_FORMULA_VERSION } from "@/features/hoverIdle/offline/offlineRewardTypes";

interface CreateGameAppOptions {
  readonly canvas: HTMLCanvasElement;
  readonly overlay: HTMLElement;
  readonly storage?: HoverIdleSaveStorage;
  readonly nowMs?: () => number;
  readonly playLogRunId?: string;
  readonly initialModelState?: HoverIdleModelState | null;
  readonly initialHovSkillState?: HovSkillState | null;
  readonly initialTimSkillState?: TimSkillState | null;
  readonly initialAchievementsState?: AchievementsState | null;
  readonly initialRecordsState?: RecordsState | null;
  readonly onStartupGameSurfaceReady?: () => Promise<void> | void;
}

export interface GameApp extends Disposable {
  start(): void;
  startPlayLogSession(input?: {
    readonly label?: string;
    readonly purpose?: string;
  }): void;
  stopPlayLogSession(): void;
  exportState(): HoverIdleModelState;
  exportHovSkillState(): HovSkillState;
  exportTimSkillState(): TimSkillState;
  exportAchievementsState(): AchievementsState;
  exportRecordsState(): RecordsState;
  exportPlayLogJsonLines(): string;
  clearPlayLog(): void;
  getPlayLogStatus(): PlayLogStatus;
}

interface HovGainUpgradePurchaseResult {
  readonly ok: boolean;
  readonly isMilestone?: boolean;
}

interface ForegroundPresentationFrame {
  readonly snapshot: HoverIdleSnapshot;
  readonly recentHovCoreRatePerHour: HugeNumber;
  readonly isStableFrameCommit: boolean;
}

const APP_ENVIRONMENT = getAppEnvironment();
// `__HOVER_IDLE_ADMIN_PANEL_BUILD__` folds to `false` in a production build, so
// every admin branch below it is removed by the bundler instead of only being
// skipped at runtime. The runtime check stays as the second, narrower gate.
const ADMIN_PANEL_AVAILABLE =
  __HOVER_IDLE_ADMIN_PANEL_BUILD__ && isAdminPanelAvailable(APP_ENVIRONMENT);
const BALANCE_LAB_AVAILABLE =
  __HOVER_IDLE_BALANCE_LAB_BUILD__ && isBalanceLabAvailable(APP_ENVIRONMENT);
const ADMIN_PURGE_AVAILABLE = isAdminPurgeAvailable(APP_ENVIRONMENT);

function toFiniteNonNegativeSafeInteger(
  value: HugeNumber,
  name: string,
): number {
  const numberValue = Number(value.toString());
  if (!Number.isSafeInteger(numberValue) || numberValue < 0) {
    throw new Error(`${name} must be a finite non-negative safe integer`);
  }
  return numberValue;
}

export async function createGameApp(
  options: CreateGameAppOptions,
): Promise<GameApp> {
  const nowMs = options.nowMs ?? Date.now;
  const mobileLayoutMediaQuery = window.matchMedia("(max-width: 720px)");
  const isTouchPrimary =
    window.matchMedia("(pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0;
  const e2eInitialStatePatch = readE2EInitialStatePatch();
  bootMark("createGameApp start");
  const playerOwner: PlayerOwner = await bootMeasureAsync(
    "resolvePlayerOwner",
    () =>
      resolvePlayerOwner({
        forceLocal: e2eInitialStatePatch !== null,
      }),
  );
  const adminRuntimePreferences = ADMIN_PANEL_AVAILABLE
    ? createLocalStorageAdminRuntimePreferenceStorage({
        ownerId: playerOwner.ownerId,
      })
    : null;
  const adminProgressBuilderSeedStorage = ADMIN_PANEL_AVAILABLE
    ? createLocalStorageAdminProgressBuilderSeedStorage()
    : null;
  const initialAdminRuntimePreferences =
    adminRuntimePreferences?.load() ?? null;
  const startupAdminGameSpeedMultiplier =
    initialAdminRuntimePreferences?.adminGameSpeedMultiplier ??
    normalizeAdminGameSpeedMultiplier(1);
  let currentOwnerEntitlementSnapshot = DEFAULT_ENTITLEMENTS;
  let adminHoverToggleDelayMsOverride: AdminHoverToggleDelayMsOverride =
    initialAdminRuntimePreferences?.adminHoverToggleDelayMsOverride ?? null;
  let showLockedHovSkillContent =
    initialAdminRuntimePreferences?.showLockedHovSkillContent ?? false;
  // createDefaultSaveStorage and createPaymentRepositories both depend only
  // on playerOwner; running them in parallel avoids one Supabase round-trip
  // on the boot critical path when cloud-backed.
  const [storage, paymentRepos] = await bootMeasureAsync(
    "createDefaultSaveStorage + createPaymentRepositories",
    () =>
      Promise.all([
        options.storage
          ? Promise.resolve(options.storage)
          : createDefaultSaveStorage(playerOwner),
        createPaymentRepositories(playerOwner),
      ]),
  );
  const { entitlementRepository, purchaseEventRepository } = paymentRepos;
  let startupOwnerEntitlements: Awaited<
    ReturnType<typeof loadEntitlementSnapshot>
  > | null = null;
  try {
    startupOwnerEntitlements = await bootMeasureAsync(
      "loadStartupEntitlementSnapshot",
      () => loadEntitlementSnapshot(playerOwner.ownerId, entitlementRepository),
    );
    currentOwnerEntitlementSnapshot = startupOwnerEntitlements.snapshot;
  } catch (error) {
    console.warn("Failed to load startup entitlements:", error);
  }
  const session = await bootMeasureAsync("createHoverIdleAppSession", () =>
    createHoverIdleAppSession({
      storage,
      nowMs,
      ownerEntitlementSnapshot: currentOwnerEntitlementSnapshot,
      startupAdminTimeScale: startupAdminGameSpeedMultiplier,
      initialModelState: options.initialModelState,
      initialHovSkillState: options.initialHovSkillState,
      initialTimSkillState: options.initialTimSkillState,
      initialAchievementsState:
        e2eInitialStatePatch?.achievements ?? options.initialAchievementsState,
      initialRecordsState: options.initialRecordsState,
      patchInitialModelState:
        e2eInitialStatePatch === null
          ? undefined
          : (state) => applyE2EInitialStatePatch(state, e2eInitialStatePatch),
      logLoadFailure: (reason) => {
        console.warn("HoverIdle save load failed:", reason);
      },
      logInitialStateFailure: (reason) => {
        console.warn("HoverIdle HMR state restore failed:", reason);
      },
      onLifecycleEvent: (event) => {
        markStartupTrace(event);
      },
    }),
  );
  bootMark("session ready");
  const model = session.model;
  setCurrentLocale(model.getSettings().language);
  setCurrentNotation(model.getSettings().notation);
  const messages = getMessages();
  const startupOfflineReward = session.startupOfflineReward;
  if (startupOfflineReward?.kind === "committed") {
    measureStartupReward(startupOfflineReward.timing);
  }
  const navItems = createGameNavItems({
    includeAdmin: ADMIN_PANEL_AVAILABLE,
    includeBalanceLab: BALANCE_LAB_AVAILABLE,
  });
  const clickNavigationScreenIds = new Set(
    navItems.filter((item) => item.click).map((item) => item.id),
  );
  const screenIdsWithCustomContent = createCustomContentScreenIds({
    includeAdmin: ADMIN_PANEL_AVAILABLE,
    includeBalanceLab: BALANCE_LAB_AVAILABLE,
  });
  let activeScreenId = "hovTarget";

  const runtime = createBabylonRuntime(options.canvas);
  bootMark("Babylon engine + scene created");
  const sceneResources = createHoverIdleScene(runtime.scene);
  const pointerModel = new HoverPointerModel();
  const pointerInputController = new HoverPointerInputController({
    model: pointerModel,
    shouldTrackVirtualPointer: () =>
      !clickNavigationScreenIds.has(activeScreenId),
  });
  const pointerView = new HoverPointerView(pointerModel);
  const hoverAnchorPersistence = createHoverAnchorPersistenceController({
    enabled: isTouchPrimary,
    model: pointerModel,
    storage: createLocalStorageHoverAnchorStorage(),
  });
  options.overlay.append(pointerView.root);
  // Effective hover-toggle duration: Admin runtime override (0 / 1000 / 2000)
  // wins over the player's gameplay setting. Read fresh on every call so the
  // next hover picks up an Admin change without a refresh round-trip. Applies
  // only to reversible 1:1 hover toggles (assign/remove, ON/OFF); charge
  // execute / submit / apply paths do not flow through this resolver.
  const getEffectiveHoverToggleDurationMs = (): number =>
    resolveAdminHoverToggleDurationMs(
      adminHoverToggleDelayMsOverride,
      model.getHoverToggleDurationMs(),
    );
  let firstPlayedAt: number = session.timestamps?.firstPlayedAt ?? nowMs();
  let lastSavedAt: number | null = session.timestamps?.savedAt ?? null;
  let offlineRewardMetadata = cloneOfflineRewardSaveMetadata(
    session.offlineReward ??
      createDefaultOfflineRewardSaveMetadata(session.timestamps?.savedAt),
  );
  let getAuthoritativeOfflineBaselineAt = (savedAtMs: number): number =>
    savedAtMs;
  const advanceSaveRevision = (
    baselineAt = offlineRewardMetadata.baselineAt,
  ) => {
    offlineRewardMetadata = advanceOfflineRewardRevision(
      offlineRewardMetadata,
      baselineAt,
    );
    return offlineRewardMetadata;
  };
  const hovSkillModel = new HovSkillModel();
  hovSkillModel.restoreState(
    e2eInitialStatePatch?.hovSkills ?? session.hovSkillState,
  );
  const timSkillModel = new TimSkillModel();
  timSkillModel.restoreState(session.timSkillState);
  // Initial sync covers entitlements already present in the local save.
  // Remote-only keys are merged in by the deferred loader.
  const initialEntitlementSync = syncEntitlementsAndApplyEffects(
    model,
    model.getPaymentState().entitlements,
  );
  const fairyModel = new FairyModel();
  const fairyView = new FairyView({
    scene: runtime.scene,
    canvas: options.canvas,
    onHoverChange: (id) => {
      fairyModel.setHoveringFairy(id);
    },
  });
  let isHovTargetActive = true;
  let isMainHoverTabActive = true;
  let activeHovTabId: CoreLoopTabId = "main-hover";
  const refreshFairyBounds = (): void => {
    const bounds = fairyView.computeWorldBounds();
    if (bounds) {
      fairyModel.setBounds(bounds);
    }
  };
  const syncFairyVisibility = (): void => {
    fairyView.setVisible(isHovTargetActive && isMainHoverTabActive);
    refreshFairyBounds();
  };

  const hovSkillEffectModifierProviders: readonly HovSkillEffectModifierProvider[] =
    [];

  const getHovSkillEffectModifiers = (): readonly HovSkillEffectModifier[] =>
    collectHovSkillEffectModifiers(hovSkillEffectModifierProviders);

  let isHovAmplifierUnlocked = false;
  let achievementPersistentEffects: AchievementPersistentEffects =
    resolveInitialGameAppAchievementPersistentEffects({
      sessionAchievementsState: session.achievementsState,
      e2eAchievementsState: e2eInitialStatePatch?.achievements,
    });
  let syncGameNavigationAvailability = (
    _snapshot: HoverIdleSnapshot = model.getSnapshot(),
  ): void => undefined;
  const getGameNavigationAvailability = (
    snapshot: HoverIdleSnapshot,
  ): GameNavigationAvailability => ({
    mpCapacity: snapshot.mpCapacity,
  });

  const syncHovSkillEffects = (): void => {
    const externalModifiers = getHovSkillEffectModifiers();
    const acquired = hovSkillModel.getSnapshot(externalModifiers).acquired;
    const skillEffects = resolveHovSkillEffects({
      acquired,
      externalModifiers,
    });

    isHovAmplifierUnlocked = skillEffects.amplifierUnlocked;

    model.setHovSkillEffects(skillEffects);

    fairyModel.setSpawnEnabled(skillEffects.fairySpawnEnabled);

    const paymentEffects = summarizePermanentPurchaseEffects(
      model.getPaymentState().purchasedPermanentItems,
    );
    model.setMpCapacity(
      toFiniteNonNegativeSafeInteger(
        skillEffects.mpCapacity
          .add(paymentEffects.mpCapacity)
          .add(achievementPersistentEffects.mpCapacityBonus),
        "hov skill MP capacity",
      ),
    );
    syncGameNavigationAvailability(model.getSnapshot());
  };

  const applyTimSkillEffect = (id: TimSkillId): void => {
    if (id === "GridIncreaseTim") {
      model.setTimGridTier(4);
    } else if (id === "GridIncreaseTim2") {
      model.setTimGridTier(9);
    }
  };

  syncHovSkillEffects();
  for (const id of timSkillModel.getSnapshot().acquired) {
    applyTimSkillEffect(id);
  }

  const achievements = new AchievementsModel();
  achievements.restoreState(
    e2eInitialStatePatch?.achievements ?? session.achievementsState,
  );
  achievementPersistentEffects = computePersistentEffects(
    achievements.exportState().unlockedIds,
  );
  const isAchievementUnlocked = (id: AchievementId): boolean =>
    achievements
      .getSnapshot()
      .entries.some((entry) => entry.definition.id === id && entry.unlocked);
  const upgradeRepeatInitialIntervals = createGameUpgradeRepeatInitialIntervals(
    () => achievementPersistentEffects,
  );
  const getUpgradeRepeatSpeedMultiplier = (): number =>
    adminGameSpeedPolicyController.getPolicy().automation
      .purchaseRepeatSpeedMultiplier;
  let isTimHovActivationUnlocked = isAchievementUnlocked("tim-hov-activation");
  const syncTimHovActivationUnlock = (): void => {
    isTimHovActivationUnlocked = isAchievementUnlocked("tim-hov-activation");
    model.setTimHovActivationUnlocked(isTimHovActivationUnlocked);
  };
  const syncAchievementPersistentEffects = (): void => {
    const ids = achievements.exportState().unlockedIds;
    achievementPersistentEffects = computePersistentEffects(ids);
    model.setAchievementPersistentEffects(achievementPersistentEffects);
    syncHovSkillEffects();
  };
  const applyAchievementOneTimeEffects = (
    newlyUnlocked: readonly AchievementId[],
  ): void => {
    for (const id of newlyUnlocked) {
      const effect = getOneTimeEffect(id);
      if (effect?.tokenGrant !== undefined && effect.tokenGrant > 0) {
        model.grantToken(effect.tokenGrant);
      }
    }
  };
  syncAchievementPersistentEffects();
  const initialNewlyUnlocked = achievements.evaluate(model.getSnapshot());
  if (initialNewlyUnlocked.length > 0) {
    applyAchievementOneTimeEffects(initialNewlyUnlocked);
    syncAchievementPersistentEffects();
  }
  syncTimHovActivationUnlock();

  const records = new RecordsModel();
  records.restoreState(session.recordsState);

  const screenCollection = createScreenCollection(
    navItems,
    screenIdsWithCustomContent,
  );
  const root = screenCollection.root;
  root.dataset.ownerKind = playerOwner.ownerKind;
  const screens = screenCollection.screens;
  const syncReduceMotionState = (): void => {
    const settings = model.getSettings();
    const motionPolicy = resolveHoverIdleMotionPolicy(settings);
    root.dataset.reduceMotion = motionPolicy.hovCellHoverPulse
      ? "false"
      : "true";
    fairyView.setReduceMotion(settings.reduceMotion);
  };

  const navCleanups: Array<() => void> = [];

  const authState = createAuthState(playerOwner);
  let accountSection: SettingsAccountSection | null = null;
  if (isSupabaseConfigured() && playerOwner.ownerKind !== "local") {
    const { getSupabaseBrowserClient } =
      await import("@/integrations/supabase/supabaseClient");
    const supabaseClient = getSupabaseBrowserClient();
    const linkService = createLinkAccountService(supabaseClient);
    accountSection = createSettingsAccountSection({
      authState,
      linkService,
    });
    const ownedAccountSection = accountSection;
    navCleanups.push(() => ownedAccountSection.dispose());
    const unsubscribeAuth = linkService.onAuthStateChange((nextOwner) => {
      if (nextOwner !== null) {
        authState.setOwner(nextOwner);
      }
    });
    navCleanups.push(unsubscribeAuth);
  }

  let navButtons: ReadonlyMap<string, HTMLButtonElement> = new Map();
  let requestRestoreHoverAnchor = (_reason: HoverAnchorRestoreReason): void =>
    undefined;
  let saveAfterImportantEvent = (): void => undefined;
  let playLogRuntime: ReturnType<typeof createPlayLogRuntime> | null = null;
  let setForegroundStandbyMode = (_enabled: boolean): void => undefined;
  let refreshNormalPresentationLayout = (): void => undefined;
  let suspendRecurringScreenPresentation = (): void => undefined;
  let resumeRecurringScreenPresentation = (): void => undefined;
  let mountBalanceLabPanel = (): void => undefined;
  let isStandbyMode = false;
  const resetHoverIdleInteractionContext = (): void => {
    pointerModel.clearVirtualTouchPosition({ preserveHoverAnchor: true });
    model.clearExplorationPointerParticipation();
  };
  const standbyNavigationToggle = createStandbyNavigationToggle({
    label: messages.standby.label,
    pointerModel,
    getChargeMs: getEffectiveHoverToggleDurationMs,
    onEnterStandby: enterStandbyMode,
    onResume: resumeFromStandbyMode,
  });
  const standbyView = createStandbyView({
    label: messages.standby.label,
    resumeAriaLabel: messages.standby.resumeAriaLabel,
    onResume: resumeFromStandbyMode,
  });
  options.overlay.append(standbyView.root, standbyNavigationToggle.standbyRoot);
  navCleanups.push(() => standbyNavigationToggle.dispose());
  navCleanups.push(() => standbyView.dispose());

  function enterStandbyMode(): void {
    if (isStandbyMode) {
      return;
    }
    isStandbyMode = true;
    standbyNavigationToggle.prepareForStandby();
    resetHoverIdleInteractionContext();
    suspendRecurringScreenPresentation();
    setForegroundStandbyMode(true);
    root.hidden = true;
    pointerView.root.hidden = true;
    options.canvas.hidden = true;
    standbyView.show();
  }

  function resumeFromStandbyMode(): void {
    if (!isStandbyMode) {
      return;
    }
    isStandbyMode = false;
    standbyView.hide();
    options.canvas.hidden = false;
    pointerView.root.hidden = false;
    root.hidden = false;
    standbyNavigationToggle.resetAfterStandby();
    refreshNormalPresentationLayout();
    setForegroundStandbyMode(false);
    resumeRecurringScreenPresentation();
    pointerModel.republish();
  }

  const helpScreen = screens.get("help")!;
  let helpView: HelpView | null = null;
  // Help reveal reads the persistent "button displayed" latches, not the
  // per-run `snapshot.{mHov,nHov}.isUnlocked`, so a deeper reset cannot hide
  // help content that was already surfaced.
  const getAvailableHelpTopicIds = (): readonly HelpTopicId[] => {
    return model.getSettings().hasSeenMHovButton ? ["mhov"] : [];
  };
  const getHelpNotificationState = (): {
    readonly isMHovUnlocked: boolean;
    readonly isNHovUnlocked: boolean;
    readonly isMHovHelpRead: boolean;
    readonly isMHovHelpUnread: boolean;
    readonly hasUnreadHelp: boolean;
  } => {
    const settings = model.getSettings();
    const availableTopicIds = getAvailableHelpTopicIds();
    const isMHovHelpRead = settings.readHelpTopicIds.includes("mhov");
    return {
      isMHovUnlocked: settings.hasSeenMHovButton,
      isNHovUnlocked: settings.hasSeenNHovButton,
      isMHovHelpRead,
      isMHovHelpUnread: isHelpTopicUnread({
        topicId: "mhov",
        helpNotificationsEnabled: settings.helpNotificationsEnabled,
        isTopicAvailable: settings.hasSeenMHovButton,
        readHelpTopicIds: settings.readHelpTopicIds,
      }),
      hasUnreadHelp: hasUnreadHelpTopic({
        helpNotificationsEnabled: settings.helpNotificationsEnabled,
        availableTopicIds,
        readHelpTopicIds: settings.readHelpTopicIds,
      }),
    };
  };
  const refreshHelpNotifications = (): void => {
    const state = getHelpNotificationState();
    const helpNavButton = navButtons.get("help");
    if (helpNavButton !== undefined) {
      const messages = getMessages();
      const label = messages.navigation.items.help.label;
      helpNavButton.dataset.unseen = state.hasUnreadHelp ? "true" : "false";
      helpNavButton.title = state.hasUnreadHelp
        ? `${label}: ${messages.help.unreadLabel}`
        : label;
      helpNavButton.setAttribute(
        "aria-label",
        state.hasUnreadHelp ? `${label}: ${messages.help.unreadLabel}` : label,
      );
    }
    helpView?.updateHelpNotificationState({
      isMHovUnlocked: state.isMHovUnlocked,
      isNHovUnlocked: state.isNHovUnlocked,
      isMHovHelpRead: state.isMHovHelpRead,
      isMHovHelpUnread: state.isMHovHelpUnread,
    });
  };
  const mountHelpView = (): void => {
    if (helpView !== null) return;
    const helpNotificationState = getHelpNotificationState();
    helpView = new HelpView({
      container: helpScreen,
      isTouchPrimary,
      isTimHovActivationUnlocked,
      isMHovUnlocked: helpNotificationState.isMHovUnlocked,
      isNHovUnlocked: helpNotificationState.isNHovUnlocked,
      isMHovHelpRead: helpNotificationState.isMHovHelpRead,
      isMHovHelpUnread: helpNotificationState.isMHovHelpUnread,
      onHelpTopicRead: (topicId) => {
        if (model.markHelpTopicRead(topicId)) {
          refreshHelpNotifications();
          saveAfterImportantEvent();
        }
      },
    });
  };
  const disposeHelpView = (): void => {
    helpView?.dispose();
    helpView = null;
  };

  const settingsScreen = screens.get("settings")!;
  let settingsPanel: SettingsPanel | null = null;

  const getSettingsPanelRuntime = (): {
    readonly isMHovUnlocked: boolean;
    readonly isNHovUnlocked: boolean;
    readonly isOHovUnlocked: boolean;
  } => {
    const snapshot = model.getSnapshot();
    const settings = model.getSettings();
    // MHov / NHov cards reveal from the persistent "button displayed" latch so
    // they cannot disappear when a deeper reset clears the per-run MHov / NHov
    // count. OHov already carries its own persistent unlock latch.
    return {
      isMHovUnlocked: settings.hasSeenMHovButton,
      isNHovUnlocked: settings.hasSeenNHovButton,
      isOHovUnlocked: snapshot.oHov.isUnlocked,
    };
  };

  const shouldShowAchievementUnseenDots = (): boolean =>
    model.getSettings().achievementNotification !== "off";

  const ensureSettingsPanel = (): void => {
    if (settingsPanel !== null) {
      settingsPanel.update(model.getSettings(), getSettingsPanelRuntime());
      return;
    }

    const panel = createSettingsPanel({
      settings: model.getSettings(),
      runtime: getSettingsPanelRuntime(),
      accountSection,
      onReduceMotionChange: (reduceMotion) => {
        const didChange = model.setReduceMotion(reduceMotion);
        if (didChange) {
          syncReduceMotionState();
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
      onLowPowerModeChange: (lowPowerMode) => {
        const didChange = model.setLowPowerMode(lowPowerMode);
        if (didChange) {
          foregroundFrameDriver.setLowPowerMode(lowPowerMode);
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
      onLanguageChange: (language) => {
        const didChange = model.setLanguage(language);
        if (!didChange) {
          return;
        }
        void saveNow().finally(() => {
          setCurrentLocale(language);
          hovView.showNotice(messages.common.languageChangedReloading);
          window.location.reload();
        });
      },
      onAchievementNotificationChange: (mode) => {
        const didChange = model.setAchievementNotification(mode);
        if (didChange) {
          achievementsView.update(achievements.getSnapshot(), {
            showUnseenDots: shouldShowAchievementUnseenDots(),
          });
          refreshAchievementsBadge();
          autosave.saveAfterImportantEvent();
        }
      },
      onWallNotificationModeChange: (mode) => {
        const didChange = model.setWallNotificationMode(mode);
        if (didChange) {
          refreshView();
          autosave.saveAfterImportantEvent();
        }
      },
      onHelpNotificationsEnabledChange: (enabled) => {
        const didChange = model.setHelpNotificationsEnabled(enabled);
        if (didChange) {
          refreshHelpNotifications();
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
      onNotationChange: (notation) => {
        const didChange = model.setNotation(notation);
        if (!didChange) {
          return;
        }
        setCurrentNotation(notation);
        achievementsView.update(achievements.getSnapshot(), {
          showUnseenDots: shouldShowAchievementUnseenDots(),
        });
        autosave.saveAfterImportantEvent();
      },
      onMHovChargeDurationChange: (value) => {
        if (model.setMHovChargeDurationMs(value)) {
          autosave.saveAfterImportantEvent();
        }
      },
      onMHovAnimationSpeedChange: (value) => {
        if (model.setMHovAnimationSpeed(value)) {
          autosave.saveAfterImportantEvent();
        }
      },
      onNHovChargeDurationChange: (value) => {
        if (model.setNHovChargeDurationMs(value)) {
          autosave.saveAfterImportantEvent();
        }
      },
      onNHovAnimationSpeedChange: (value) => {
        if (model.setNHovAnimationSpeed(value)) {
          autosave.saveAfterImportantEvent();
        }
      },
      onOHovChargeDurationChange: (value) => {
        if (model.setOHovChargeDurationMs(value)) {
          autosave.saveAfterImportantEvent();
        }
      },
      onOutsideResetBurstAnimationChange: (enabled) => {
        if (model.setOutsideResetBurstAnimationEnabled(enabled)) {
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
      onHoverToggleDurationChange: (value) => {
        if (model.setHoverToggleDuration(value)) {
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
      onHoverChargeDurationChange: (value) => {
        if (model.setHoverChargeDuration(value)) {
          settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
          autosave.saveAfterImportantEvent();
        }
      },
    });

    settingsPanel = panel;
    settingsScreen.replaceChildren(panel.root);
    navCleanups.push(() => panel.dispose());

    const created = createDataPanel({
      container: panel.dataContainer,
      loadPaymentState: () => model.getPaymentState(),
      onBackup: handleBackup,
      onLoadFile: handleLoadFile,
      onRedeemRewardCode: handleRedeemRewardCode,
      onEraseProgress: handleEraseProgress,
    });
    dataPanel = created;
    navCleanups.push(() => created.dispose());

    panel.update(model.getSettings(), getSettingsPanelRuntime());
  };

  const switchScreen = (id: string): void => {
    const snapshot = model.getSnapshot();
    const nextScreenId = resolveAvailableGameScreenId(
      id,
      getGameNavigationAvailability(snapshot),
    );
    if (nextScreenId === activeScreenId) return;
    if (activeScreenId === "hovTarget") {
      model.setHoveringHov(false);
    }
    if (activeScreenId === "timTarget") {
      model.setHoveringTim(false);
    }
    if (activeScreenId === "records") {
      recordsView.notifyHidden();
    }
    if (activeScreenId === "achievements") {
      achievementsView.notifyHidden();
    }
    if (activeScreenId === "help") {
      disposeHelpView();
    }
    screens.get(activeScreenId)!.hidden = true;
    if (nextScreenId === "help") {
      mountHelpView();
    }
    if (nextScreenId === "balanceLab") {
      mountBalanceLabPanel();
    }
    screens.get(nextScreenId)!.hidden = false;
    syncNavigationActiveState(navButtons, nextScreenId);
    activeScreenId = nextScreenId;
    isHovTargetActive = nextScreenId === "hovTarget";
    model.setHovTargetScreenActive(isHovTargetActive);
    resetHoverIdleInteractionContext();
    explorationView.setVisible(
      isHovTargetActive && activeHovTabId === "hov-exploration",
    );
    syncFairyVisibility();
    requestRestoreHoverAnchor("screen-change");
    if (nextScreenId === "achievements") {
      achievementsView.notifyShown();
    }
    if (nextScreenId === "records") {
      recordsView.notifyShown();
    }
  };

  syncGameNavigationAvailability = (
    snapshot: HoverIdleSnapshot = model.getSnapshot(),
  ): void => {
    const availability = getGameNavigationAvailability(snapshot);
    for (const item of navItems) {
      const button = navButtons.get(item.id);
      if (button === undefined) continue;
      button.hidden = !isGameNavItemVisible(item, availability);
    }
    const nextScreenId = resolveAvailableGameScreenId(
      activeScreenId,
      availability,
    );
    if (nextScreenId !== activeScreenId) {
      switchScreen(nextScreenId);
    }
  };

  const navigation = createNavigationElements({
    isTouchPrimary,
    items: navItems,
    activeScreenId,
    switchScreen,
    ensureSettingsPanel,
    standbyToggle: standbyNavigationToggle.element,
  });
  navButtons = navigation.buttons;
  navCleanups.push(...navigation.cleanups);
  navCleanups.push(disposeHelpView);
  syncGameNavigationAvailability();

  root.append(navigation.root);
  refreshHelpNotifications();
  syncReduceMotionState();
  options.overlay.append(root);

  const hovTargetScreen = screens.get("hovTarget")!;
  const timTargetScreen = screens.get("timTarget")!;
  let hovView: HovView;
  let timView: TimView;
  let wallView: WallView;
  let explorationView: ExplorationView;
  let tutorialOverlay: FirstAccessTutorialOverlay | null = null;
  let saveQueue = Promise.resolve();
  let isOrdinarySaveSuspendedForRewardCommit = false;
  // The recent Hov rate is fed the Hov actually earned each tick
  // (model.getHovEarnedThisTick()), not a balance/total delta. Reconstructing
  // the gain from a delta rounds to zero in break_eternity once the running
  // total dwarfs the per-tick increment (e.g. a 1e324 gain against 1e359).
  const recentHovCoreRateClock = createRecentHovCoreRateClock(nowMs());
  const initialRateObservedAtMs = recentHovCoreRateClock.nowMs();
  const initialRateSnapshot = model.getSnapshot();
  const recentHovRateTracker = createRecentHovRateTracker();
  const completeRecentHovRateSegment = (
    beforeSnapshot: {
      readonly mHov: {
        readonly currentLoopHovEarned: HugeNumber;
      };
    },
    afterSnapshot: {
      readonly mHov: {
        readonly currentLoopHovEarned: HugeNumber;
      };
    },
    finalGameplayElapsedSeconds = 0,
  ) =>
    recentHovRateTracker.completeSegmentAndReset({
      segmentEndCurrentLoopHovEarned: beforeSnapshot.mHov.currentLoopHovEarned,
      nextCurrentLoopHovEarned: afterSnapshot.mHov.currentLoopHovEarned,
      finalGameplayElapsedSeconds,
    });
  const recentHovCoreRateTracker = createRecentHovCoreRateTracker(
    initialRateSnapshot.oHov.hovCore,
    initialRateObservedAtMs,
  );
  let amplifierUpgradeAutomation: MpmAmplifierUpgradeAutomationController | null =
    null;
  const autoResetTargetPresentationGate =
    createAutoResetTargetPresentationGate();
  const hovSurfaceReadoutSnapshots = createHovSurfaceReadoutSnapshotMemory();
  let scheduleAutoResetHovTargetHoverRefresh = (): void => undefined;
  let rescheduleAutoResetHovTargetHoverRefresh = (): void => undefined;
  let invalidateAutoResetHovTargetHoverRefresh = (): void => undefined;

  const syncUpgradeMpAutomations = (snapshot: HoverIdleSnapshot): void => {
    amplifierUpgradeAutomation?.sync(snapshot);
  };

  const shouldShowWallTabDot = (snapshot: HoverIdleSnapshot): boolean => {
    if (model.getSettings().wallNotificationMode === "off") return false;
    if (!snapshot.wall.wallUnlocked) return false;
    return snapshot.wall.unseenClearedWallIds.length > 0;
  };

  const notifyNewlyClearedWalls = (
    beforeSnapshot: HoverIdleSnapshot,
    afterSnapshot: HoverIdleSnapshot,
  ): boolean => {
    const result = dispatchWallClearNotifications({
      beforeSnapshot,
      afterSnapshot,
      mode: model.getSettings().wallNotificationMode,
      activeHovTabId,
      showToast: (wallId) => {
        const messages = getMessages();
        const notice =
          messages.wall.walls[wallId]?.clearedNotice ??
          messages.wall.clearedNotice;
        hovView.showNotice(notice, {
          dismissAfterHoverMs: 100,
        });
      },
      markSeen: (wallId) => model.markWallClearSeen(wallId),
    });
    return result.markedSeen;
  };

  const notifyAutoOHovClearedWalls = (
    beforeSnapshot: HoverIdleSnapshot | null,
    afterSnapshot: HoverIdleSnapshot,
  ): boolean => {
    if (beforeSnapshot === null) return false;
    return notifyNewlyClearedWalls(beforeSnapshot, afterSnapshot);
  };

  const updateHovView = (
    snapshot: HoverIdleSnapshot,
    recentHovCoreRatePerHour: HugeNumber,
    options: {
      readonly isStableFrameCommit?: boolean;
      readonly suppressTargetStructureCommit?: boolean;
    } = {},
  ): void => {
    const targetPresentation = autoResetTargetPresentationGate.resolveUpdate({
      ...options,
      hasPresentationWorthyTargetStructure: snapshot.hovGridTier !== 1,
      suppressTargetStructureCommit:
        options.suppressTargetStructureCommit === true ||
        isOfflineRewardCommitInProgress,
    });
    const readoutPresentation =
      hovSurfaceReadoutSnapshots.resolveReadoutPresentation({
        authoritativeSnapshot: snapshot,
        holdAutoResetReadout:
          targetPresentation.targetStructureAction ===
          "hold-last-presentation-worthy",
      });
    try {
      hovView.update(
        snapshot,
        recentHovRateTracker.getPresentationRate(
          readoutPresentation.recentHovRateSegmentToken,
        ),
        recentHovCoreRatePerHour,
        {
          readoutSnapshot: readoutPresentation.snapshot,
          targetStructureSnapshot: snapshot,
          targetStructureAction: targetPresentation.targetStructureAction,
          updateTargetThemeHue: targetPresentation.updateTargetThemeHue,
          deferTargetStructureHoverRefresh:
            targetPresentation.scheduleHoverRefreshAfterStructureCommit,
        },
      );
    } finally {
      autoResetTargetPresentationGate.completeUpdate(targetPresentation);
    }
    if (targetPresentation.scheduleHoverRefreshAfterStructureCommit) {
      scheduleAutoResetHovTargetHoverRefresh();
    }
  };

  const refreshView = (snapshot = model.getSnapshot()): void => {
    const viewObservedAtMs = recentHovCoreRateClock.nowMs();
    recentHovCoreRateTracker.observe(viewObservedAtMs, snapshot.oHov.hovCore);
    const recentHovCoreRatePerHour =
      recentHovCoreRateTracker.getRate(viewObservedAtMs);
    updateHovView(snapshot, recentHovCoreRatePerHour);
    timView.update(snapshot, recentHovRateTracker.getRate());
    mpmView.update(snapshot);
    wallView.refresh(snapshot);
    if (activeHovTabId === "hov-exploration") {
      explorationView.update(toExplorationScreenSnapshot(snapshot));
    }
    hovView.setWallTabDot(shouldShowWallTabDot(snapshot));
    syncUpgradeMpAutomations(snapshot);
  };

  const toExplorationScreenSnapshot = (
    snapshot: HoverIdleSnapshot,
  ): ExplorationScreenSnapshot => ({
    gameplay: snapshot,
    exploration: model.getExplorationDetailsSnapshot(),
  });

  const restoreHoverAnchorForActiveSurface = (
    _reason: HoverAnchorRestoreReason,
  ): void => {
    if (activeScreenId === "hovTarget") {
      if (activeHovTabId === "hov-exploration") {
        explorationView.restoreVirtualPointerFromHoverAnchor();
      } else {
        hovView.restoreVirtualPointerFromHoverAnchor();
      }
    } else if (activeScreenId === "timTarget") {
      timView.restoreVirtualPointerFromHoverAnchor();
    }
  };

  const hoverAnchorRestoreController = createHoverAnchorRestoreController({
    enabled: isTouchPrimary,
    restore: restoreHoverAnchorForActiveSurface,
  });
  requestRestoreHoverAnchor = (reason): void => {
    hoverAnchorRestoreController.requestRestore(reason);
  };

  const canRefreshHovTargetHoverIntents = (): boolean =>
    activeScreenId === "hovTarget" && activeHovTabId === "main-hover";

  const enqueueCurrentSave = (propagateFailure = false): Promise<void> => {
    const operation = saveQueue
      .catch(() => undefined)
      .then(async () => {
        const timestamp = nowMs();
        const offlineBaselineAt = getAuthoritativeOfflineBaselineAt(timestamp);
        lastSavedAt = timestamp;
        const saveData = createHoverIdleSaveData({
          state: model.exportState(),
          hovSkills: hovSkillModel.exportState(),
          timSkills: timSkillModel.exportState(),
          achievements: achievements.exportState(),
          records: records.exportState(),
          timestamps: {
            savedAt: timestamp,
            firstPlayedAt,
          },
          offlineReward: advanceSaveRevision(offlineBaselineAt),
        });
        await storage.save(saveData);
      });
    const settled = operation.catch((error: unknown) => {
      console.warn("HoverIdle save failed:", reasonFromUnknown(error));
    });
    saveQueue = settled;
    return propagateFailure ? operation : settled;
  };

  const persistCurrentSave = (): Promise<void> => enqueueCurrentSave();

  const saveNow = (): Promise<void> =>
    isOrdinarySaveSuspendedForRewardCommit ? saveQueue : persistCurrentSave();

  const autosave = createHoverIdleAutosaveController({
    saveNow,
    saveOnVisibilityHidden: false,
  });
  saveAfterImportantEvent = (): void => autosave.saveAfterImportantEvent();

  const hovGainUpgradePurchaseSideEffects =
    createHovGainUpgradePurchaseSideEffectBatcher({
      refreshView,
      requestAutosave: () => autosave.saveAfterImportantEvent(),
      recordMilestonePurchases: (
        purchases: readonly HovGainUpgradeMilestonePurchaseSideEffectEvent[],
        snapshot: HoverIdleSnapshot,
      ) => {
        playLogRuntime?.recordHovGainUpgradePurchaseBatch({
          purchases,
          snapshot,
        });
      },
    });

  const pulseMpmHovGainUpgradeTarget = (
    target: HovGainUpgradeMpTarget,
    isMilestone: boolean,
  ): void => {
    hovView.pulseHovGainUpgradeTarget(target, {
      isMilestone,
    });
  };

  const refreshAfterFailedHovGainUpgradePurchase = (
    snapshot: HoverIdleSnapshot,
  ): void => {
    if (hovGainUpgradePurchaseSideEffects.hasPending()) {
      hovGainUpgradePurchaseSideEffects.flush(snapshot);
      return;
    }
    refreshView(snapshot);
  };

  const recordBulkSuccessfulPurchase = (
    target: HovGainUpgradeMpTarget,
    result: HovGainUpgradeBulkPurchaseResult,
  ): void => {
    for (const milestone of result.milestonePurchases) {
      hovGainUpgradePurchaseSideEffects.recordSuccessfulPurchase({
        target,
        isMilestone: true,
        enhancementLevelBefore: milestone.enhancementLevelBefore,
        enhancementLevelAfter: milestone.enhancementLevelAfter,
        snapshot: result.snapshot,
      });
    }
    // Ensure the final snapshot is captured even when no milestones occurred,
    // and that the latest snapshot wins if the milestone loop above ran.
    hovGainUpgradePurchaseSideEffects.recordSuccessfulPurchase({
      target,
      isMilestone: false,
      enhancementLevelBefore: result.enhancementLevelBefore,
      enhancementLevelAfter: result.enhancementLevelAfter,
      snapshot: result.snapshot,
    });
  };

  const purchaseGlobalHovGainUpgrade = (): HovGainUpgradePurchaseResult => {
    const result = model.purchaseGlobalHovGainUpgradeOneCostBatch();
    if (!result.ok) {
      refreshAfterFailedHovGainUpgradePurchase(result.snapshot);
      return { ok: false };
    }
    recordBulkSuccessfulPurchase("global", result);
    return {
      ok: true,
      isMilestone: result.milestonePurchases.length > 0,
    };
  };

  const purchaseHovCellGainUpgrade = (
    index: HovCellGainUpgradeIndex,
  ): HovGainUpgradePurchaseResult => {
    const result = model.purchaseHovCellGainUpgradeOneCostBatch(index);
    if (!result.ok) {
      refreshAfterFailedHovGainUpgradePurchase(result.snapshot);
      return { ok: false };
    }
    recordBulkSuccessfulPurchase({ kind: "cell", index }, result);
    return {
      ok: true,
      isMilestone: result.milestonePurchases.length > 0,
    };
  };

  amplifierUpgradeAutomation = createMpmAmplifierUpgradeAutomationController({
    initialIntervalMs: upgradeRepeatInitialIntervals.amplifierUpgrade,
    speedMultiplier: () =>
      adminGameSpeedPolicyController.getPolicy().amplifier
        .upgradeAutomationSpeedMultiplier,
    eventBudgetPerTick: () =>
      adminGameSpeedPolicyController.getPolicy().amplifier
        .upgradeAutomationEventBudget,
    onPurchaseAmplifierUpgrade: (entryId) =>
      model.purchaseHovAmplifierGainUpgrade(entryId).ok,
    onPurchasesApplied: () => {
      refreshView();
      autosave.saveAfterImportantEvent();
    },
  });

  const achievementsScreen = screens.get("achievements")!;
  const achievementsNavButton = navButtons.get("achievements") ?? null;
  const mpmScreen = screens.get("mpm")!;
  const mpmView = new MpmView({
    container: mpmScreen,
    pointerModel,
    onMpAssign: (cellIndex) => {
      if (model.placeMpAtTargetIndex(cellIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMpRemove: (cellIndex) => {
      if (model.retrieveMp(cellIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradeMpAssign: (target) => {
      if (model.assignMpToHovGainUpgradeTarget(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradeMpRemove: (target) => {
      if (model.removeMpFromHovGainUpgradeTarget(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelMpAssign: (entryId) => {
      if (model.assignMpToAmplifierPanel(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelMpRemove: (entryId) => {
      if (model.removeMpFromAmplifierPanel(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradeMpAssign: (entryId) => {
      if (model.assignMpToAmplifierUpgrade(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradeMpRemove: (entryId) => {
      if (model.removeMpFromAmplifierUpgrade(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onExplorationMpAssign: (slotId) => {
      const result = model.assignExplorationMpSlot(slotId);
      refreshView();
      if (result.ok) autosave.saveAfterImportantEvent();
    },
    onExplorationMpRemove: (slotId) => {
      const result = model.removeExplorationMpSlot(slotId);
      refreshView();
      if (result.ok) autosave.saveAfterImportantEvent();
    },
    onMHovExecutionMpAssign: () => {
      if (model.assignMpToMHovExecution()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMHovExecutionMpRemove: () => {
      if (model.removeMpFromMHovExecution()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onNHovExecutionMpAssign: (branchId) => {
      if (model.assignMpToNHovExecution(branchId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onNHovExecutionMpRemove: (branchId) => {
      if (model.removeMpFromNHovExecution(branchId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onOHovExecutionMpAssign: () => {
      if (model.assignMpToOHovExecution()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onOHovExecutionMpRemove: () => {
      if (model.removeMpFromOHovExecution()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMHovExecutionMultiplierSave: (value) => {
      if (model.setMpmMHovExecutionMultiplierInput(value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onMHovExecutionMinimumResetIntervalMsSave: (value) => {
      if (model.setMpmMHovExecutionMinimumResetIntervalMs(value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onNHovExecutionMinimumEffectSave: (branchId, value) => {
      if (model.setMpmNHovExecutionMinimumEffectInput(branchId, value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onNHovExecutionMinimumTimeSecondsSave: (branchId, value) => {
      if (model.setMpmNHovExecutionMinimumTimeSeconds(branchId, value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onOHovExecutionMinimumHovCoreSave: (value) => {
      if (model.setMpmOHovExecutionMinimumHovCoreInput(value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onOHovExecutionMinimumIntervalMsSave: (value) => {
      if (model.setMpmOHovExecutionMinimumIntervalMs(value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      } else {
        refreshView();
      }
    },
    onMHovExecutionEnabledChange: (enabled) => {
      if (model.setMpmMHovExecutionEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onNHovExecutionEnabledChange: (enabled) => {
      if (model.setMpmNHovExecutionEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onOHovExecutionEnabledChange: (enabled) => {
      if (model.setMpmOHovExecutionEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovTargetsClearAll: () => {
      if (model.clearMpmHovTargetAssignments()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradesClearAll: () => {
      if (model.clearMpmHovGainUpgradeAssignments()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelsClearAll: () => {
      if (model.clearMpmAmplifierPanelAssignments()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradesClearAll: () => {
      if (model.clearMpmAmplifierUpgradeAssignments()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onExplorationClearAll: () => {
      if (model.clearMpmExplorationAssignments()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelsAssignAll: () => {
      if (model.assignAllMpmAmplifierPanels()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovTargetsAssignAll: () => {
      if (model.assignAllMpmHovTargets()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradesAssignAll: () => {
      if (model.assignAllMpmHovGainUpgrades()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onExplorationAssignAll: () => {
      if (model.assignAllMpmExploration()) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovTargetsEnabledChange: (enabled) => {
      if (model.setMpmHovTargetsEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradesEnabledChange: (enabled) => {
      if (model.setMpmHovGainUpgradesEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelsEnabledChange: (enabled) => {
      if (model.setMpmAmplifierPanelsEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradesEnabledChange: (enabled) => {
      if (model.setMpmAmplifierUpgradesEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onExplorationEnabledChange: (enabled) => {
      if (model.setMpmExplorationEnabled(enabled)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onPresetSave: (presetIndex) => {
      if (model.saveMpmPreset(presetIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onPresetLoad: (presetIndex) => {
      if (model.loadMpmPreset(presetIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    getMpmPresetSlotCount: () => model.getMpmPresetSlotCount(),
    getHoverToggleDurationMs: getEffectiveHoverToggleDurationMs,
    getHoverChargeDurationMs: () => model.getHoverChargeDurationMs(),
  });
  navCleanups.push(() => mpmView.dispose());

  const refreshAchievementsBadge = (): void => {
    if (!achievementsNavButton) return;
    achievementsNavButton.dataset.unseen =
      shouldShowAchievementUnseenDots() && achievements.hasUnseen()
        ? "true"
        : "false";
  };

  const achievementsView = new AchievementsView({
    container: achievementsScreen,
    onAchievementSeen: (id) => {
      if (achievements.markSeen(id)) {
        achievementsView.update(achievements.getSnapshot(), {
          showUnseenDots: shouldShowAchievementUnseenDots(),
        });
        refreshAchievementsBadge();
        autosave.saveAfterImportantEvent();
      }
    },
    onTabOpened: (category) => {
      if (achievements.markUnlockedSeenByCategory(category)) {
        achievementsView.update(achievements.getSnapshot(), {
          showUnseenDots: shouldShowAchievementUnseenDots(),
        });
        refreshAchievementsBadge();
        autosave.saveAfterImportantEvent();
      }
    },
  });
  achievementsView.update(achievements.getSnapshot(), {
    showUnseenDots: shouldShowAchievementUnseenDots(),
  });
  refreshAchievementsBadge();
  navCleanups.push(() => achievementsView.dispose());

  const recordsScreen = screens.get("records")!;
  const recordsView = new RecordsView({
    container: recordsScreen,
    getStatsSnapshot: () =>
      computeStatsSnapshot({
        modelSnapshot: model.getSnapshot(),
        achievementsSnapshot: achievements.getSnapshot(),
        firstPlayedAt,
        lastSavedAt,
        nowMs: nowMs(),
        notation: model.getSettings().notation,
      }),
  });
  recordsView.update(records.getSnapshot());
  suspendRecurringScreenPresentation = (): void => {
    if (activeScreenId === "records") {
      recordsView.notifyHidden();
    }
  };
  resumeRecurringScreenPresentation = (): void => {
    if (activeScreenId === "records") {
      recordsView.notifyShown();
    }
  };
  navCleanups.push(() => recordsView.dispose());

  const evaluateAchievements = (): void => {
    const newlyUnlocked = achievements.evaluate(model.getSnapshot());
    if (newlyUnlocked.length === 0) return;
    applyAchievementOneTimeEffects(newlyUnlocked);
    syncAchievementPersistentEffects();
    syncTimHovActivationUnlock();
    playLogRuntime?.recordAchievementUnlock(newlyUnlocked, model.getSnapshot());
    if (activeScreenId === "help") {
      disposeHelpView();
      mountHelpView();
    }
    achievementsView.markRecentlyUnlocked(newlyUnlocked);
    const mode = model.getSettings().achievementNotification;
    if (mode === "off") {
      achievementsView.update(achievements.getSnapshot(), {
        showUnseenDots: shouldShowAchievementUnseenDots(),
      });
      refreshAchievementsBadge();
      autosave.saveAfterImportantEvent();
      return;
    }
    achievementsView.update(achievements.getSnapshot(), {
      showUnseenDots: shouldShowAchievementUnseenDots(),
    });
    refreshAchievementsBadge();
    if (mode === "full") {
      const messages = getMessages();
      const names = newlyUnlocked
        .map((id) => {
          const def = achievements.getDefinition(id);
          if (def?.category === "secret") {
            return messages.achievements.secrets.names[def.id];
          }
          if (def?.category === "normal") {
            return messages.achievements.names[def.id];
          }
          return "";
        })
        .filter((name) => name !== "")
        .join(", ");
      hovView.showNotice(messages.achievements.unlockedNotice(names), {
        dismissAfterHoverMs: 100,
        groupKey: ACHIEVEMENT_NOTIFICATION_TOAST_GROUP,
        maxVisibleCount: ACHIEVEMENT_NOTIFICATION_MAX_VISIBLE_COUNT,
      });
    }
    autosave.saveAfterImportantEvent();
  };

  const buildAdminStateBundle = (): HoverIdleAdminStateBundle => ({
    modelState: model.exportState(),
    hovSkillState: hovSkillModel.exportState(),
    timSkillState: timSkillModel.exportState(),
    achievementsState: achievements.exportState(),
  });

  const applyAdminStateBundle = (
    incomingBundle: HoverIdleAdminStateBundle,
    applyOptions?: {
      readonly afterModelsSynced?: () => void;
      readonly restoreMode?: "edit" | "checkpoint";
      readonly deferAutosave?: boolean;
    },
  ): HoverIdleAdminStateBundle => {
    applyAdminStateBundleToModels({
      model,
      achievements,
      hovSkillModel,
      timSkillModel,
      incomingBundle,
      restoreMode: applyOptions?.restoreMode,
      syncAchievementPersistentEffects,
      syncTimHovActivationUnlock,
    });
    recentHovRateTracker.reset();
    recentHovCoreRateTracker.reset(
      model.getSnapshot().oHov.hovCore,
      recentHovCoreRateClock.nowMs(),
    );
    fairyModel.reset();
    syncEntitlementsAndApplyEffects(
      model,
      model.getPaymentState().entitlements,
    );

    syncHovSkillEffects();
    for (const id of timSkillModel.getSnapshot().acquired) {
      applyTimSkillEffect(id);
    }

    // MP capacity and unlocks are authoritative here, so any post-apply MPM
    // adjustment (e.g. Progress Builder overlaying the captured assignment
    // intent) runs before the view refresh and save below.
    applyOptions?.afterModelsSynced?.();

    syncReduceMotionState();
    foregroundFrameDriver.setLowPowerMode(model.getSettings().lowPowerMode);
    settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
    refreshView();
    syncPaymentPanels();
    evaluateAchievements();
    achievementsView.update(achievements.getSnapshot(), {
      showUnseenDots: shouldShowAchievementUnseenDots(),
    });
    refreshAchievementsBadge();
    if (!applyOptions?.deferAutosave) autosave.saveAfterImportantEvent();

    return buildAdminStateBundle();
  };

  // Progress Builder is a verification state builder. It rebuilds progression
  // from a seed but must not discard the MP layout the user arranged: capture
  // the current assignment intent before the rebuild, then overlay it back onto
  // the post-apply state. Capacity / unlock / active resolution come from the
  // rebuilt progress; only over-capacity intent is stripped (shared priority).
  const applyProgressBuilderState = (
    incomingBundle: HoverIdleAdminStateBundle,
  ): HoverIdleAdminStateBundle => {
    const capturedIntent = model.getMpmAssignmentIntent();
    return applyAdminStateBundle(incomingBundle, {
      afterModelsSynced: () => {
        model.applyMpmAssignmentIntentOverlay(
          capturedIntent,
          "progress-builder-apply",
        );
      },
    });
  };

  let dataPanel: DataPanel | null = null;
  let paymentPanel: PaymentPanel | null = null;
  const syncPaymentPanels = (): void => {
    const paymentState = model.getPaymentState();
    dataPanel?.update(paymentState);
    paymentPanel?.update(paymentState);
  };

  const paymentScreen = screens.get("payment")!;
  const handleBackup = async (): Promise<SaveBackupTextResult> => {
    const timestamp = nowMs();
    const saveData = createHoverIdleSaveData({
      state: model.exportState(),
      hovSkills: hovSkillModel.exportState(),
      timSkills: timSkillModel.exportState(),
      achievements: achievements.exportState(),
      records: records.exportState(),
      timestamps: {
        savedAt: timestamp,
        firstPlayedAt,
      },
      offlineReward: {
        ...cloneOfflineRewardSaveMetadata(offlineRewardMetadata),
        baselineAt: timestamp,
      },
    });
    const text = encodeHoverIdleBackupText(saveData);
    const filename = buildHoverIdleBackupFilename({ nowMs: timestamp });
    return saveBackupText(filename, text);
  };

  const handleLoadFile = async (file: File): Promise<DataPanelLoadResult> => {
    const text = await file.text();
    const decoded = decodeHoverIdleBackupText(text);
    if (!decoded.ok) {
      return { ok: false, reason: decoded.reason };
    }
    const timestamp = nowMs();
    const replaced = {
      ...decoded.data,
      timestamps: {
        savedAt: timestamp,
        firstPlayedAt: decoded.data.timestamps.firstPlayedAt,
      },
      offlineReward: advanceOfflineRewardRevision(
        decoded.data.offlineReward,
        timestamp,
      ),
    };
    autosave.dispose();
    try {
      await saveQueue.catch(() => undefined);
      await storage.save(replaced);
    } catch (error) {
      return { ok: false, reason: reasonFromUnknown(error) };
    }
    window.location.reload();
    return { ok: true };
  };

  const handleRedeemRewardCode = (code: string) => {
    const result = model.redeemRewardCode(code);
    syncPaymentPanels();
    if (result.ok) {
      autosave.saveAfterImportantEvent();
    }
    return result;
  };

  const handlePurchasePermanentItem = (id: PermanentPurchaseId) => {
    const result = model.purchasePermanentItem(id);
    syncPaymentPanels();
    if (result.ok) {
      syncHovSkillEffects();
      refreshView();
      autosave.saveAfterImportantEvent();
    }
    return result;
  };

  const purchaseDeps = () => ({
    ownerId: playerOwner.ownerId,
    entitlementRepository,
    purchaseEventRepository,
  });

  const handleCheckoutBundle = async (id: BundleEntitlementKey) => {
    if (model.hasEntitlement(id)) {
      return {
        ok: false as const,
        reason: "already-owned" as const,
        payment: model.getPaymentState(),
      };
    }

    const result = await purchaseShopItem(id, purchaseDeps());
    if (!result.ok || result.kind !== "bundle") {
      return {
        ok: false as const,
        reason: "checkout-failed" as const,
        payment: model.getPaymentState(),
      };
    }

    const entitlementSync = syncEntitlementsAndApplyEffects(
      model,
      result.entitlements.bundleKeys,
    );
    if (entitlementSync.didChange) {
      records.recordPurchase({
        timestamp: nowMs(),
        productId: id,
      });
      recordsView.update(records.getSnapshot());
    }
    syncHovSkillEffects();
    refreshView();
    syncPaymentPanels();
    evaluateAchievements();
    achievementsView.update(achievements.getSnapshot(), {
      showUnseenDots: shouldShowAchievementUnseenDots(),
    });
    if (entitlementSync.didChange) {
      autosave.saveAfterImportantEvent();
    }

    if (!model.hasEntitlement(id)) {
      return {
        ok: false as const,
        reason: "checkout-failed" as const,
        payment: model.getPaymentState(),
      };
    }
    return {
      ok: true as const,
      payment: model.getPaymentState(),
    };
  };

  const handleCheckoutTokenPack = async (id: TokenPackId) => {
    const result = await purchaseShopItem(id, purchaseDeps());
    if (!result.ok || result.kind !== "tokenPack") {
      return {
        ok: false as const,
        reason: "checkout-failed" as const,
        payment: model.getPaymentState(),
      };
    }
    model.grantToken(result.tokenGrant);
    const purchaseTimestamp = nowMs();
    records.recordPurchase({
      timestamp: purchaseTimestamp,
      productId: id,
    });
    records.recordTokenChange({
      timestamp: purchaseTimestamp,
      delta: result.tokenGrant,
      reason: "tokenPack",
      productId: id,
    });
    recordsView.update(records.getSnapshot());
    syncPaymentPanels();
    refreshView();
    autosave.saveAfterImportantEvent();
    return {
      ok: true as const,
      payment: model.getPaymentState(),
    };
  };

  const handleEraseProgress =
    async (): Promise<DataPanelEraseProgressResult> => {
      const timestamp = nowMs();
      const freshState = new HoverIdleModel().exportState();
      const resetState: HoverIdleModelState = {
        ...freshState,
        settings: applyProgressEraseToSettings(model.getSettings()),
        payment: model.getPaymentState(),
      };
      const freshSave = createHoverIdleSaveData({
        state: resetState,
        hovSkills: createEmptyHovSkillState(),
        timSkills: createEmptyTimSkillState(),
        achievements: emptyAchievementsState,
        records: records.exportState(),
        timestamps: {
          savedAt: timestamp,
          firstPlayedAt: null,
        },
        offlineReward: advanceSaveRevision(timestamp),
      });
      autosave.dispose();
      try {
        await saveQueue.catch(() => undefined);
        await storage.save(freshSave);
      } catch (error) {
        return { ok: false, reason: reasonFromUnknown(error) };
      }
      window.location.reload();
      return { ok: true };
    };

  const handlePurgeUserData = async (): Promise<{
    readonly ok: boolean;
    readonly reason?: string;
    readonly deletedScopes?: readonly string[];
    readonly localOnly?: boolean;
    readonly serverFailureReason?: string;
  }> => {
    const result = await purgeOwnUserData();
    const allowLocalFallback = APP_ENVIRONMENT === "dev";
    const localOnly = !result.ok && allowLocalFallback;
    if (!result.ok && !allowLocalFallback) {
      return { ok: false, reason: result.reason };
    }
    const timestamp = nowMs();
    const freshState = new HoverIdleModel().exportState();
    const wipedSave = createHoverIdleSaveData({
      state: {
        ...freshState,
        settings: applyAccountPurgeToSettings(),
      },
      hovSkills: createEmptyHovSkillState(),
      timSkills: createEmptyTimSkillState(),
      achievements: emptyAchievementsState,
      records: new RecordsModel().exportState(),
      timestamps: {
        savedAt: timestamp,
        firstPlayedAt: null,
      },
      offlineReward: advanceSaveRevision(timestamp),
    });
    autosave.dispose();
    try {
      await saveQueue.catch(() => undefined);
      await storage.save(wipedSave);
    } catch (error) {
      return {
        ok: false,
        reason: reasonFromUnknown(error),
        deletedScopes: result.deletedScopes,
      };
    }
    window.location.reload();
    return {
      ok: true,
      deletedScopes: result.deletedScopes,
      localOnly,
      serverFailureReason: localOnly ? result.reason : undefined,
    };
  };

  const requestSignIn = (): void => {
    switchScreen("settings");
    ensureSettingsPanel();
    settingsPanel?.setActiveTab("data");
  };

  paymentPanel = createPaymentPanel({
    container: paymentScreen,
    loadState: () => model.getPaymentState(),
    onPurchaseEffect: handlePurchasePermanentItem,
    onCheckoutBundle: handleCheckoutBundle,
    onCheckoutTokenPack: handleCheckoutTokenPack,
    getOwnerKind: () => authState.getSnapshot().owner.ownerKind,
    onRequestSignIn: requestSignIn,
  });
  navCleanups.push(() => paymentPanel?.dispose());
  syncPaymentPanels();

  let isRunning = false;
  let isDisposed = false;
  let isOfflineRewardCommitInProgress = false;
  let isAdminCheckpointRestoring = false;
  let adminCheckpoints: AdminCheckpointController | null = null;
  let nextRuntimeFrameBaselineAtMs: number | null = null;
  let isCommittedResultAwaitingFirstPaint = false;
  let deferredEntitlementTimerId: number | null = null;

  const adminGameSpeedPolicyController = createAdminGameSpeedPolicyController(
    startupAdminGameSpeedMultiplier,
  );
  const saveAdminRuntimePreferences = (): void => {
    adminRuntimePreferences?.save({
      adminGameSpeedMultiplier: adminGameSpeedPolicyController.get(),
      adminHoverToggleDelayMsOverride,
      showLockedHovSkillContent,
    });
  };
  const pendingSameSessionCatchUps: AdminGameSpeedElapsedPolicy[] = [];
  let pendingSameSessionCatchUpRawElapsedMs = 0;
  const absenceLifecycle = createAbsenceLifecycleCoordinator({
    captureCheckpoint: saveNow,
    resume: ({ unprocessedElapsedMs }) => {
      if (unprocessedElapsedMs <= 0) {
        return;
      }
      const catchUp = adminGameSpeedPolicyController.resolveSameSessionCatchUp(
        unprocessedElapsedMs / 1000,
      );
      pendingSameSessionCatchUps.push(catchUp);
      pendingSameSessionCatchUpRawElapsedMs += unprocessedElapsedMs;
    },
    onError: (error) => {
      console.error(
        "Failed to persist same-page background checkpoint:",
        error,
      );
    },
  });
  getAuthoritativeOfflineBaselineAt = (savedAtMs: number): number => {
    const unprocessedElapsedMs =
      absenceLifecycle.getUnprocessedElapsedMs(savedAtMs) +
      pendingSameSessionCatchUpRawElapsedMs;
    return resolveSamePageOfflineBaselineAt({
      previousBaselineAtMs: offlineRewardMetadata.baselineAt,
      savedAtMs,
      unprocessedElapsedMs,
    });
  };
  const getEffectiveContinuousAdminSpeed = (): number =>
    adminGameSpeedPolicyController.getPolicy().continuous
      .effectiveSpeedMultiplier;
  playLogRuntime = createPlayLogRuntime({
    runId: options.playLogRunId,
    nowMs,
    getSnapshot: () => model.getSnapshot(),
    getSpeedMultiplier: getEffectiveContinuousAdminSpeed,
    canRecordCalibrationTrace: ADMIN_PANEL_AVAILABLE,
    getHovSkillSummary: () => {
      const externalModifiers = getHovSkillEffectModifiers();
      const acquired = hovSkillModel.getSnapshot(externalModifiers).acquired;
      const resolvedEffects = resolveHovSkillEffects({
        acquired,
        externalModifiers,
      });
      return buildPlayLogHovSkillSummary({ acquired, resolvedEffects });
    },
  });
  navCleanups.push(
    model.subscribeMpmNumericSettingsChanges((changes) => {
      playLogRuntime.recordMpmNumericSettingsChange(changes);
    }),
  );
  playLogRuntime.startSession({
    startedAtMs: nowMs(),
    initialSpeedMultiplier: getEffectiveContinuousAdminSpeed(),
    label: "hover-idle-runtime",
    purpose: "real-play-analysis",
  });
  const recordOfflineReward = (
    rewardOutcome: Extract<
      ProductionOfflineRewardOutcome,
      { readonly kind: "committed" }
    >,
    source: "startup-reward" | "admin-offline-reward",
  ): void => {
    playLogRuntime.recordOfflineRewardApply({
      source,
      realElapsedMs: rewardOutcome.elapsed.realElapsedMs,
      uncappedGameElapsedMs: rewardOutcome.elapsed.uncappedGameElapsedMs,
      gameElapsedMs: rewardOutcome.elapsed.rewardElapsedMs,
      wasCapped: rewardOutcome.elapsed.wasCapped,
      rewardId: rewardOutcome.result.rewardId,
      rewardFormulaVersion: rewardOutcome.input.rewardFormulaVersion,
      timGained: rewardOutcome.result.timDelta,
      resultDigest: rewardOutcome.result.resultDigest,
      commitRevision: rewardOutcome.result.receipt.commitRevision,
      inputPreparationMs: rewardOutcome.timing.inputPreparationMs,
      mainThreadSingleTaskMs: rewardOutcome.timing.mainThreadSingleTaskMs,
      mainThreadTotalBlockingMs: rewardOutcome.timing.mainThreadTotalBlockingMs,
      endToEndMs: rewardOutcome.timing.endToEndMs,
      snapshot: model.getSnapshot(),
    });
  };
  if (startupOfflineReward?.kind === "committed") {
    recordOfflineReward(startupOfflineReward, "startup-reward");
  }

  const startPlayLogSession = (input?: {
    readonly label?: string;
    readonly purpose?: string;
  }): void => {
    playLogRuntime?.startSession({
      startedAtMs: nowMs(),
      initialSpeedMultiplier: getEffectiveContinuousAdminSpeed(),
      label: input?.label ?? "admin-manual",
      purpose: input?.purpose ?? "real-play-analysis",
    });
  };

  const stopPlayLogSession = (): void => {
    playLogRuntime?.stopSession({ endedAtMs: nowMs() });
  };

  const exportPlayLogJsonLines = (): string =>
    playLogRuntime?.exportPlayLogJsonLines() ?? "";

  const exportPlayLogCompactJsonLines = (): string =>
    playLogRuntime?.exportPlayLogCompactJsonLines() ?? "";

  const clearPlayLog = (): void => {
    playLogRuntime?.clear();
  };

  const getPlayLogStatus = (): PlayLogStatus =>
    playLogRuntime?.getStatus() ?? {
      isRecording: false,
      runId: null,
      entriesCount: 0,
      speedMultiplier: getEffectiveContinuousAdminSpeed(),
    };

  let adminPanel: AdminPanel | null = null;
  let isAdminPanelLoading = false;
  const adminCheckpointsReady =
    __HOVER_IDLE_ADMIN_PANEL_BUILD__ && ADMIN_PANEL_AVAILABLE
      ? Promise.all([
          import("@/features/admin/checkpoints/adminCheckpointController"),
          import("@/features/admin/checkpoints/adminCheckpointRepository"),
        ]).then(
          ([
            { createAdminCheckpointController, rebaseAdminCheckpointSave },
            { createAdminCheckpointRepository },
          ]) => {
            if (isDisposed) return;
            adminCheckpoints = createAdminCheckpointController({
              repository: createAdminCheckpointRepository(),
              nowMs,
              capture: () =>
                createHoverIdleSaveData({
                  state: model.exportState(),
                  hovSkills: hovSkillModel.exportState(),
                  timSkills: timSkillModel.exportState(),
                  achievements: achievements.exportState(),
                  records: records.exportState(),
                  timestamps: { savedAt: nowMs(), firstPlayedAt },
                  offlineReward: cloneOfflineRewardSaveMetadata(
                    offlineRewardMetadata,
                  ),
                }),
              setRestorePending: (pending) => {
                isAdminCheckpointRestoring = pending;
                root.inert = pending;
                resetHoverIdleInteractionContext();
                nextRuntimeFrameBaselineAtMs = performance.now();
              },
              apply: async (saved) => {
                if (isDisposed) throw new Error("Game runtime disposed");
                const save = rebaseAdminCheckpointSave(saved, nowMs());
                applyAdminStateBundle(
                  {
                    modelState: modelStateFromHoverIdleSaveData(save),
                    hovSkillState: hovSkillStateFromHoverIdleSaveData(save),
                    timSkillState: timSkillStateFromHoverIdleSaveData(save),
                    achievementsState:
                      achievementsStateFromHoverIdleSaveData(save),
                  },
                  { restoreMode: "checkpoint", deferAutosave: true },
                );
                records.restoreState(recordsStateFromHoverIdleSaveData(save));
                recordsView.update(records.getSnapshot());
                firstPlayedAt = save.timestamps.firstPlayedAt ?? nowMs();
                offlineRewardMetadata = advanceOfflineRewardRevision(
                  offlineRewardMetadata,
                  nowMs(),
                );
                // Discard elapsed from the abandoned attempt rather than advancing the restored one.
                absenceLifecycle.handleResume(nowMs());
                pendingSameSessionCatchUps.length = 0;
                pendingSameSessionCatchUpRawElapsedMs = 0;
                if (document.visibilityState === "hidden")
                  absenceLifecycle.handleHidden(nowMs());
                await enqueueCurrentSave(true);
              },
            });
            return adminCheckpoints.ready;
          },
        )
      : null;
  const mountAdminPanel = (): void => {
    if (!__HOVER_IDLE_ADMIN_PANEL_BUILD__) {
      return;
    }
    if (!ADMIN_PANEL_AVAILABLE || adminPanel !== null || isAdminPanelLoading) {
      return;
    }
    const adminScreen = screens.get("admin");
    if (!(adminScreen instanceof HTMLElement)) {
      return;
    }

    isAdminPanelLoading = true;
    void Promise.all([
      import("@/features/admin/adminPanel"),
      adminCheckpointsReady,
    ])
      .then(([{ createAdminPanel }]) => {
        isAdminPanelLoading = false;
        if (isDisposed || adminPanel !== null) {
          return;
        }

        adminPanel = createAdminPanel({
          container: adminScreen,
          environment: APP_ENVIRONMENT,
          loadState: buildAdminStateBundle,
          applyState: applyAdminStateBundle,
          checkpoints: adminCheckpoints ?? undefined,
          applyProgressBuilderState,
          simulateOfflineMinutes: (minutes) => {
            return simulateOfflineMinutes(minutes);
          },
          applyHov: (amount) => {
            const current = buildAdminStateBundle();
            applyAdminStateBundle({
              ...current,
              modelState: {
                ...current.modelState,
                economy: {
                  ...current.modelState.economy,
                  hov: amount,
                },
              },
            });
          },
          applyHovCore: (amount) => {
            const current = buildAdminStateBundle();
            applyAdminStateBundle({
              ...current,
              modelState: {
                ...current.modelState,
                economy: {
                  ...current.modelState.economy,
                  oHov: {
                    ...current.modelState.economy.oHov,
                    hovCore: amount,
                  },
                },
              },
            });
          },
          adminGameSpeed: {
            get: () => adminGameSpeedPolicyController.get(),
            getPolicy: () => adminGameSpeedPolicyController.getPolicy(),
            set: (value) => {
              const previousEffectiveContinuousSpeed =
                getEffectiveContinuousAdminSpeed();
              adminGameSpeedPolicyController.set(value);
              const nextEffectiveContinuousSpeed =
                getEffectiveContinuousAdminSpeed();
              nextRuntimeFrameBaselineAtMs = performance.now();
              playLogRuntime?.recordSpeedChange({
                from: previousEffectiveContinuousSpeed,
                to: nextEffectiveContinuousSpeed,
              });
              saveAdminRuntimePreferences();
            },
          },
          hoverToggleDelayOverride: {
            get: () => adminHoverToggleDelayMsOverride,
            set: (value) => {
              adminHoverToggleDelayMsOverride = value;
              saveAdminRuntimePreferences();
            },
          },
          lockedHovSkillContentVisibility: {
            get: () => showLockedHovSkillContent,
            set: (value) => {
              showLockedHovSkillContent = value;
              saveAdminRuntimePreferences();
              refreshView();
            },
          },
          pointerModel,
          getHoverToggleDurationMs: () => model.getHoverToggleDurationMs(),
          playLog: {
            getStatus: getPlayLogStatus,
            startSession: startPlayLogSession,
            stopSession: stopPlayLogSession,
            exportJsonLines: exportPlayLogJsonLines,
            exportCompactJsonLines: exportPlayLogCompactJsonLines,
            clear: clearPlayLog,
          },
          onPurgeUserData: ADMIN_PURGE_AVAILABLE
            ? handlePurgeUserData
            : undefined,
          progressBuilderSeedStorage:
            adminProgressBuilderSeedStorage ?? undefined,
        });
      })
      .catch((error: unknown) => {
        isAdminPanelLoading = false;
        console.warn("Failed to load admin panel:", reasonFromUnknown(error));
      });
  };
  if (ADMIN_PANEL_AVAILABLE) {
    navCleanups.push(() => adminPanel?.dispose());
  }

  let balanceLabPanel: BalanceLabPanel | null = null;
  let isBalanceLabPanelLoading = false;
  mountBalanceLabPanel = (): void => {
    if (!__HOVER_IDLE_BALANCE_LAB_BUILD__) {
      return;
    }
    if (
      !BALANCE_LAB_AVAILABLE ||
      balanceLabPanel !== null ||
      isBalanceLabPanelLoading
    ) {
      return;
    }
    const balanceLabScreen = screens.get("balanceLab");
    if (!(balanceLabScreen instanceof HTMLElement)) {
      return;
    }

    isBalanceLabPanelLoading = true;
    void import("@/features/balanceLab/balanceLabPanel")
      .then(({ createBalanceLabPanel }) => {
        isBalanceLabPanelLoading = false;
        if (isDisposed || balanceLabPanel !== null) {
          return;
        }
        balanceLabPanel = createBalanceLabPanel({
          container: balanceLabScreen,
        });
      })
      .catch((error: unknown) => {
        isBalanceLabPanelLoading = false;
        console.warn("Failed to load Balance Lab:", reasonFromUnknown(error));
      });
  };
  if (BALANCE_LAB_AVAILABLE) {
    navCleanups.push(() => balanceLabPanel?.dispose());
  }

  const toggleTimHovActivation = (): void => {
    if (!isAchievementUnlocked("tim-hov-activation")) {
      return;
    }
    const snapshot = model.getSnapshot();
    if (model.setTimHovActivationEnabled(!snapshot.isTimHovActivationEnabled)) {
      refreshView();
      autosave.saveAfterImportantEvent();
    }
  };

  hovView = new HovView({
    container: hovTargetScreen,
    pointerModel,
    onCellHoverChange: (cellIndex, isHovering) => {
      let accepted = true;
      if (isHovering) {
        accepted = model.setHoveringCellIndex(cellIndex);
      } else if (model.getSnapshot().hovHoveringCellIndex === cellIndex) {
        accepted = model.setHoveringCellIndex(null);
      }
      refreshView();
      return accepted;
    },
    onPurchaseGlobalHovGainUpgrade: () => purchaseGlobalHovGainUpgrade(),
    onPurchaseHovCellGainUpgrade: (index: HovCellGainUpgradeIndex) =>
      purchaseHovCellGainUpgrade(index),
    resolveGlobalHovGainUpgradeOneCostBatchPreview: () =>
      model.previewGlobalHovGainUpgradeOneCostBatch(),
    resolveHovCellGainUpgradeOneCostBatchPreview: (index) =>
      model.previewHovCellGainUpgradeOneCostBatch(index),
    onHovGainUpgradePurchaseBurstStart: () => {
      hovGainUpgradePurchaseSideEffects.beginBurst();
    },
    onHovGainUpgradePurchaseBurstEnd: () => {
      hovGainUpgradePurchaseSideEffects.endBurst();
    },
    getHovGainFamilyUpgradeRepeatInitialIntervalMs:
      upgradeRepeatInitialIntervals.hovGainFamilyUpgrade,
    getAmplifierUpgradeRepeatInitialIntervalMs:
      upgradeRepeatInitialIntervals.amplifierUpgrade,
    getUpgradeRepeatSpeedMultiplier,
    getHovSkillSnapshot: () =>
      hovSkillModel.getSnapshot(getHovSkillEffectModifiers()),
    getHovSkillHoldDurationMs: () => model.getSettings().hovSkillHoldDurationMs,
    getShowLockedHovSkillContent: () => showLockedHovSkillContent,
    getHovAmplifierUnlocked: () => isHovAmplifierUnlocked,
    getWallUnlocked: () => model.isWallUnlocked(),
    getExplorationAvailable: () => model.isExplorationUnlocked(),
    getOHovHovCoreGainScalingUnlocked: () =>
      achievementPersistentEffects.ohovHovCoreGainScalingUnlocked,
    onHovSkillHoldDurationChange: (ms: HovSkillHoldMs) => {
      if (model.setHovSkillHoldDurationMs(ms)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onActiveTabChange: (tabId) => {
      resetHoverIdleInteractionContext();
      isMainHoverTabActive = tabId === "main-hover";
      activeHovTabId = tabId;
      explorationView.setVisible(tabId === "hov-exploration");
      if (tabId === "hov-exploration") {
        explorationView.update(
          toExplorationScreenSnapshot(model.getSnapshot()),
        );
      }
      if (tabId === "hov-wall") {
        const unseenWallIds = [...model.getUnseenClearedWallIds()];
        let markedAny = false;
        for (const wallId of unseenWallIds) {
          markedAny = model.markWallClearSeen(wallId) || markedAny;
        }
        if (markedAny) {
          refreshView();
          autosave.saveAfterImportantEvent();
        }
      }
      syncFairyVisibility();
      requestRestoreHoverAnchor("tab-change");
    },
    onMpWallHovTargetAssign: (cellIndex) => {
      if (model.assignMpWallHovTarget(cellIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMpWallHovTargetRemove: (cellIndex) => {
      if (model.removeMpWallHovTarget(cellIndex)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMpWallHovGainUpgradeAssign: (target) => {
      if (model.assignMpWallHovGainUpgrade(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMpWallHovGainUpgradeRemove: (target) => {
      if (model.removeMpWallHovGainUpgrade(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMpPlace: (cellIndex) => {
      if (model.placeMp(cellIndex)) {
        refreshView();
      }
    },
    onMpRetrieve: (cellIndex) => {
      if (model.retrieveMp(cellIndex)) {
        refreshView();
      }
    },
    onHovGainUpgradeMpAssign: (target) => {
      if (model.assignMpToHovGainUpgradeTarget(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onHovGainUpgradeMpRemove: (target) => {
      if (model.removeMpFromHovGainUpgradeTarget(target)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onMHovExecute: () => {
      // Capture only the pre-reset fields the PlayLog payload reads. The full
      // HoverIdleSnapshot build that would normally happen here fans out into
      // 9-cell, pipeline, and NHov derivations that the reset discards before
      // any UI sees them. The slim capture leaves the post-reset getSnapshot()
      // as the single heavy build per MHov execution.
      const beforeSnapshot = model.getMHovExecuteBeforeSnapshot();
      const result = model.executeMHov();
      const afterSnapshot = result.snapshot;
      if (result.ok) {
        completeRecentHovRateSegment(beforeSnapshot, afterSnapshot);
      }
      refreshView(afterSnapshot);
      playLogRuntime?.recordMHovExecute({
        ok: result.ok,
        executionSource: "manual",
        beforeSnapshot,
        snapshot: afterSnapshot,
      });
      autosave.saveAfterImportantEvent();
    },
    getMotionPolicy: () => resolveHoverIdleMotionPolicy(model.getSettings()),
    getMHovSpeed: () => model.getMHovAnimationSpeed(),
    getMHovChargeDurationMs: () => model.getMHovChargeDurationMs(),
    onNHovExecute: (branchId) => {
      const beforeSnapshot = model.getSnapshot();
      const didExecute = model.executeNHov(branchId);
      if (didExecute) {
        const afterSnapshot = model.getSnapshot();
        completeRecentHovRateSegment(beforeSnapshot, afterSnapshot);
        refreshView(afterSnapshot);
        playLogRuntime?.recordNHovExecute({
          branchId,
          ok: true,
          beforeSnapshot,
          snapshot: afterSnapshot,
        });
        autosave.saveAfterImportantEvent();
      } else {
        playLogRuntime?.recordNHovExecute({
          branchId,
          ok: false,
          beforeSnapshot,
        });
      }
      return didExecute;
    },
    onOHovExecute: () => {
      const beforeSnapshot = model.getSnapshot();
      const didExecute = model.executeOHov();
      if (didExecute) {
        const afterSnapshot = model.getSnapshot();
        completeRecentHovRateSegment(beforeSnapshot, afterSnapshot);
        const markedWallClearSeen = notifyNewlyClearedWalls(
          beforeSnapshot,
          afterSnapshot,
        );
        refreshView(markedWallClearSeen ? undefined : afterSnapshot);
        playLogRuntime?.recordOHovExecute({
          ok: true,
          executionSource: "manual",
          beforeSnapshot,
          snapshot: afterSnapshot,
        });
        autosave.saveAfterImportantEvent();
      } else {
        playLogRuntime?.recordOHovExecute({
          ok: false,
          executionSource: "manual",
          beforeSnapshot,
        });
      }
    },
    onLockHovInput: (locked) => {
      if (locked) {
        model.setHoveringCellIndex(null);
        refreshView();
      }
    },
    onHovCellClick: () => {
      model.recordHovCellClick();
      evaluateAchievements();
      refreshView();
    },
    onHovOLabelHoverChange: (isHovering) => {
      model.setHoveringHovOLabel(isHovering);
      refreshView();
    },
    onHovAmplifierEntryHoverChange: (entryId, isHovering) => {
      model.setHoveringHovAmplifierEntry(isHovering ? entryId : null);
    },
    onPurchaseHovAmplifierGainUpgrade: (entryId) => {
      const result = model.purchaseHovAmplifierGainUpgrade(entryId);
      if (result.ok) {
        refreshView(result.snapshot);
        autosave.saveAfterImportantEvent();
      }
      return result.ok;
    },
    onAmplifierPanelMpAssign: (entryId) => {
      if (model.assignMpToAmplifierPanel(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierPanelMpRemove: (entryId) => {
      if (model.removeMpFromAmplifierPanel(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradeMpAssign: (entryId) => {
      if (model.assignMpToAmplifierUpgrade(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    onAmplifierUpgradeMpRemove: (entryId) => {
      if (model.removeMpFromAmplifierUpgrade(entryId)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    getAmplifierUpgradeGuardMs: () => model.getAmplifierUpgradeGuardMs(),
    onAmplifierUpgradeGuardMsChange: (value) => {
      if (model.setAmplifierUpgradeGuardMs(value)) {
        refreshView();
        autosave.saveAfterImportantEvent();
      }
    },
    getHasSeenAvailableAmplifierUpgradeGuard: () =>
      model.getSettings().hasSeenAvailableAmplifierUpgradeGuard,
    onSeenAvailableAmplifierUpgradeGuard: () => {
      const changed = model.markSeenAvailableAmplifierUpgradeGuard();
      if (changed) {
        autosave.saveAfterImportantEvent();
      }
      return changed;
    },
    getHasSeenMHovButton: () => model.getSettings().hasSeenMHovButton,
    onSeenMHovButton: () => {
      const changed = model.markSeenMHovButton();
      if (changed) {
        autosave.saveAfterImportantEvent();
      }
      return changed;
    },
    getHasSeenNHovButton: () => model.getSettings().hasSeenNHovButton,
    onSeenNHovButton: () => {
      const changed = model.markSeenNHovButton();
      if (changed) {
        autosave.saveAfterImportantEvent();
      }
      return changed;
    },
    getHovOLabelAchievementUnlocked: () => isAchievementUnlocked("hov-o-label"),
    getTimHovActivationUnlocked: () =>
      isAchievementUnlocked("tim-hov-activation"),
    isHngDrawerActive: () => mobileLayoutMediaQuery.matches,
    onTimHovActivationToggle: toggleTimHovActivation,
    getHoverToggleDurationMs: getEffectiveHoverToggleDurationMs,
    onHovSkillAcquire: (id) => {
      const snapshot = model.getSnapshot();
      const result = hovSkillModel.purchase(
        id,
        toHovSkillResourceBalances(snapshot),
        spendHovSkillCost,
      );
      if (result.ok) {
        syncHovSkillEffects();
        refreshView();
        autosave.saveAfterImportantEvent();
        return true;
      }
      refreshView();
      return false;
    },
  });
  hovView.seedPresentationWorthyTargetStructure(initialRateSnapshot);

  const wallActionHandlers = createWallActionHandlers({
    startWall: (wallId) => {
      const beforeSnapshot = model.getSnapshot();
      const ok = model.startWall(wallId);
      if (ok) {
        const afterSnapshot = model.getSnapshot();
        completeRecentHovRateSegment(beforeSnapshot, afterSnapshot);
        // Record the wall-start OHov-equivalent boundary as an
        // `ohov-execute` event tagged with executionSource "wall-start" so
        // PlayLog analysis can distinguish it from manual / auto / mp
        // executions and from wall clears (wall clear events have
        // beforeActiveWallId set and afterActiveWallId null; wall starts are
        // the inverse).
        playLogRuntime?.recordOHovExecute({
          ok: true,
          executionSource: "wall-start",
          beforeSnapshot,
          snapshot: afterSnapshot,
        });
      }
      return ok;
    },
    stopActiveWall: () => model.stopActiveWall(),
    refreshView: () => refreshView(),
    saveAfterImportantEvent: () => autosave.saveAfterImportantEvent(),
    showHovTargetScreen: () => {
      switchScreen("hovTarget");
      hovView.showHovTargetScreen();
    },
    waitForHovTargetLayout: () => waitForPaint(),
    canRefreshHovTargetHoverIntents,
    getPointerSnapshot: () => pointerModel.getSnapshot(),
    restoreVirtualPointerPosition: (position) => {
      pointerModel.setVirtualTouchPosition(position, {
        hoverAnchorTargetId: null,
      });
    },
    refreshHovTargetHoverIntents: () => {
      hovView.refreshHovTargetHoverIntents();
    },
  });
  wallView = new WallView({
    container: hovView.getWallTabContainer(),
    pointerModel,
    onStartWall: wallActionHandlers.onStartWall,
    onStopWall: wallActionHandlers.onStopWall,
    getWallActionChargeDurationMs: () =>
      model.getSettings().wallStartChargeDurationMs,
    startStopChargeDurationSetting: {
      options: WALL_START_CHARGE_MS_OPTIONS,
      onChange: (ms: number) => {
        if (model.setWallStartChargeDurationMs(ms as WallStartChargeMs)) {
          refreshView();
          autosave.saveAfterImportantEvent();
        }
      },
    },
  });
  navCleanups.push(() => wallView.dispose());

  const executeExplorationCommand = (
    command: () => ExplorationModelCommandResult,
  ): ExplorationModelCommandResult => {
    const result = command();
    refreshView();
    if (result.ok) {
      autosave.saveAfterImportantEvent();
    }
    return result;
  };
  explorationView = new ExplorationView({
    container: hovView.getExplorationTabContainer(),
    initialSnapshot: toExplorationScreenSnapshot(initialRateSnapshot),
    onPurchaseMpSlot: () =>
      executeExplorationCommand(() => model.purchaseExplorationMpSlot()),
    onAssignMpSlot: (slotId) =>
      executeExplorationCommand(() => model.assignExplorationMpSlot(slotId)),
    onRemoveMpSlot: (slotId) =>
      executeExplorationCommand(() => model.removeExplorationMpSlot(slotId)),
    pointerModel,
    getHoverToggleDurationMs: getEffectiveHoverToggleDurationMs,
    onBiomeHoverChange: (biomeId, isHovering) => {
      const changed = model.setExplorationBiomeHovering(biomeId, isHovering);
      if (changed) refreshView();
    },
    onTimAccelerationEnabledChange: (enabled) => {
      const changed = model.setExplorationTimAccelerationEnabled(enabled);
      refreshView();
      if (changed) autosave.saveAfterImportantEvent();
      return model.getExplorationDetailsSnapshot();
    },
  });
  explorationView.setVisible(false);
  navCleanups.push(() => explorationView.dispose());

  const outsideResetBurstController = new OutsideResetBurstController({
    overlayContainer: hovTargetScreen,
    getOrigin: () => hovView.getOutsideResetOrigin(),
  });
  const outsideResetBurstScheduler = new OutsideResetBurstScheduler();

  function toHovSkillResourceBalances(
    snapshot: HoverIdleSnapshot,
  ): HovSkillResourceBalances {
    return {
      hovCore: snapshot.oHov.hovCore,
    };
  }

  function spendHovSkillCost(cost: HovSkillCost): boolean {
    switch (cost.resource) {
      case "hovCore":
        return model.spendHovCore(cost.amount);
    }
  }

  timView = new TimView({
    container: timTargetScreen,
    pointerModel,
    onCellHoverChange: (cellIndex, isHovering) => {
      let accepted = true;
      if (isHovering) {
        accepted = model.setTimHoveringCellIndex(cellIndex);
      } else if (model.getSnapshot().timHoveringCellIndex === cellIndex) {
        accepted = model.setTimHoveringCellIndex(null);
      }
      refreshView();
      return accepted;
    },
    onUpgrade: () => {
      const result = model.purchaseTimGainUpgrade();
      refreshView();
      if (result.ok) {
        autosave.saveAfterImportantEvent();
      }

      return result.ok;
    },
    onUpgradeCell: (cellIndex) => {
      const result = model.purchaseTimCellUpgrade(cellIndex);
      refreshView();
      if (result.ok) {
        autosave.saveAfterImportantEvent();
      }

      return result.ok;
    },
    getUpgradeRepeatInitialIntervalMs:
      upgradeRepeatInitialIntervals.defaultUpgrade,
    getUpgradeRepeatSpeedMultiplier,
    getTimSkillSnapshot: () => timSkillModel.getSnapshot(),
    onHovTimBalanceAssign: (target) => {
      if (model.assignHovTimBalance(target)) {
        refreshView();
      }
    },
    onHovTimBalanceUnassign: (target) => {
      if (model.unassignHovTimBalance(target)) {
        refreshView();
      }
    },
    onHovTimBalanceRatioChange: (pair, ratio) => {
      if (model.setHovTimBalanceRatio(pair, ratio)) {
        refreshView();
      }
    },
    onActiveTabChange: () => {
      resetHoverIdleInteractionContext();
      requestRestoreHoverAnchor("tab-change");
    },
    onHovOLabelHoverChange: (isHovering) => {
      model.setHoveringHovOLabel(isHovering);
      refreshView();
    },
    getHovOLabelAchievementUnlocked: () => isAchievementUnlocked("hov-o-label"),
    getTimHovActivationUnlocked: () =>
      isAchievementUnlocked("tim-hov-activation"),
    onTimHovActivationToggle: toggleTimHovActivation,
    getHoverToggleDurationMs: getEffectiveHoverToggleDurationMs,
    onTimSkillAcquire: (id) => {
      const snapshot = model.getSnapshot();
      if (!timSkillModel.canAcquire(id, snapshot.tim)) {
        return false;
      }
      const def = TimSkillModel.listDefinitions().find((d) => d.id === id);
      if (!def) return false;
      const costTim = huge(def.costTim);
      if (!model.spendTim(costTim)) return false;
      const result = timSkillModel.acquire(id, costTim);
      if (result.ok) {
        applyTimSkillEffect(id);
        refreshView();
        autosave.saveAfterImportantEvent();
        return true;
      }
      refreshView();
      return false;
    },
  });

  tutorialOverlay = new FirstAccessTutorialOverlay({
    container: root,
    pointerModel,
    isTouchPrimary,
    onComplete: () => {
      if (model.setFirstAccessTutorialCompleted(true)) {
        autosave.saveAfterImportantEvent();
      }
      if (isTouchPrimary) {
        hovView.showNotice(messages.tutorial.mobileNotice, {
          dismissAfterHoverMs: 100,
        });
      }
    },
    onLanguageChange: (language) => {
      const didChange = model.setLanguage(language);
      if (didChange) {
        autosave.saveAfterImportantEvent();
        refreshView();
      }
    },
  });
  navCleanups.push(() => tutorialOverlay?.dispose());
  if (!model.getSettings().firstAccessTutorialCompleted) {
    tutorialOverlay.show();
  }

  const resize = (): void => {
    if (isStandbyMode) {
      return;
    }
    runtime.engine.resize();
    refreshFairyBounds();
    requestRestoreHoverAnchor("resize");
  };
  refreshNormalPresentationLayout = resize;

  const autoResetHovTargetHoverRefresh =
    createPostPaintHovTargetHoverRefreshScheduler({
      waitForPaint,
      canRefreshHovTargetHoverIntents,
      refreshHovTargetHoverIntents: () => {
        hovView.refreshHovTargetHoverIntents();
      },
    });

  scheduleAutoResetHovTargetHoverRefresh = (): void => {
    autoResetHovTargetHoverRefresh.schedule();
  };

  rescheduleAutoResetHovTargetHoverRefresh = (): void => {
    autoResetHovTargetHoverRefresh.rescheduleAfterReset();
  };

  invalidateAutoResetHovTargetHoverRefresh = (): void => {
    autoResetHovTargetHoverRefresh.invalidate();
  };

  type AutoResetExecutionEvent = ReturnType<
    HoverIdleModel["consumeLastAutoResetExecutionEvents"]
  >[number];
  type HovGainUpgradeMpAutomationEvent = ReturnType<
    HoverIdleModel["consumeLastHovGainUpgradeMpAutomationEvents"]
  >[number];
  type AutoResetReadoutSnapshots = ReturnType<
    HoverIdleModel["consumeLastAutoResetExecuteBeforeSnapshots"]
  >;

  const captureAutoResetReadoutSnapshots = (options: {
    readonly recoverLiveHovTargetInput: boolean;
    readonly authoritativeSnapshot: HoverIdleSnapshot;
    readonly finalGameplayElapsedSeconds: number | null;
    readonly events: readonly AutoResetExecutionEvent[];
    readonly readoutSnapshots: AutoResetReadoutSnapshots;
  }): boolean => {
    const selectedLayer: AutoResetReadoutLayer | null =
      options.events[options.events.length - 1]?.layer ?? null;
    if (selectedLayer === null) {
      return false;
    }
    const selectedSnapshot =
      selectedLayer === "mhov"
        ? options.readoutSnapshots.mHov
        : selectedLayer === "nhov"
          ? options.readoutSnapshots.nHov
          : options.readoutSnapshots.oHov;
    if (selectedSnapshot === null) {
      return false;
    }

    const recentHovRateSegmentToken =
      options.finalGameplayElapsedSeconds === null
        ? null
        : completeRecentHovRateSegment(
            selectedSnapshot,
            options.authoritativeSnapshot,
            options.finalGameplayElapsedSeconds,
          );
    if (options.finalGameplayElapsedSeconds === null) {
      recentHovRateTracker.reset();
    }
    hovSurfaceReadoutSnapshots.latchAutoResetSnapshot({
      layer: selectedLayer,
      snapshot: selectedSnapshot,
      recentHovRateSegmentToken,
    });
    autoResetTargetPresentationGate.markAutoResetExecuted(selectedLayer);
    if (options.recoverLiveHovTargetInput) {
      rescheduleAutoResetHovTargetHoverRefresh();
    } else {
      invalidateAutoResetHovTargetHoverRefresh();
    }
    return true;
  };

  const prepareAutoResetTargetPresentation = (options: {
    readonly recoverLiveHovTargetInput: boolean;
    readonly authoritativeSnapshot: HoverIdleSnapshot;
    readonly finalGameplayElapsedSeconds: number | null;
    readonly events: readonly AutoResetExecutionEvent[];
    readonly readoutSnapshots: AutoResetReadoutSnapshots;
  }) => {
    const didCompleteRecentHovRateSegment =
      captureAutoResetReadoutSnapshots(options);
    return {
      didCompleteRecentHovRateSegment,
    };
  };

  const handleGameplayAutomationSideEffects = (
    resetEvents: readonly AutoResetExecutionEvent[],
    hguEvents: readonly HovGainUpgradeMpAutomationEvent[],
    finalSnapshot: HoverIdleSnapshot,
    options: {
      readonly ohovAutoSource: "online-update" | "offline-progress";
      readonly ohovMpOfflineSource: "offline-progress" | null;
      readonly deferViewRefresh?: boolean;
      readonly deferAutosave?: boolean;
    },
  ): HoverIdleSnapshot => {
    return commitGameplayAutomationBurstSideEffects({
      resetEvents,
      hguEvents,
      finalSnapshot,
      ports: {
        recordPlayLogItem: (item) => {
          recordGameplayAutomationPlayLogItem(item, options);
        },
        recordHguMilestones: (milestones, snapshot) => {
          playLogRuntime?.recordHovGainUpgradePurchaseBatch({
            purchases: milestones,
            snapshot,
          });
        },
        pulseHguTarget: pulseMpmHovGainUpgradeTarget,
        notifyOHovWallClears: (events, snapshot) => {
          const firstBeforeSnapshot = events[0]?.beforeSnapshot ?? null;
          return notifyAutoOHovClearedWalls(firstBeforeSnapshot, snapshot);
        },
        refreshView:
          options.deferViewRefresh === true ? () => undefined : refreshView,
        requestAutosave:
          options.deferAutosave === true
            ? () => undefined
            : () => autosave.saveAfterImportantEvent(),
        getFinalSnapshot: () => model.getSnapshot(),
      },
    });
  };

  const recordGameplayAutomationPlayLogItem = (
    item: GameplayAutomationBurstPlayLogItem,
    options: {
      readonly ohovAutoSource: "online-update" | "offline-progress";
      readonly ohovMpOfflineSource: "offline-progress" | null;
      readonly deferViewRefresh?: boolean;
      readonly deferAutosave?: boolean;
    },
  ): void => {
    if (item.kind === "summary") {
      playLogRuntime?.recordGameplayAutomationBurst({
        summary: item.summary,
      });
      return;
    }
    const event = item.event;
    if (event.kind === "ohov-auto-execution") {
      playLogRuntime?.recordOHovAutoExecute({
        source: options.ohovAutoSource,
        executionSource: "auto",
        beforeSnapshot: event.beforeSnapshot,
        snapshot: event.afterSnapshot,
      });
      return;
    }
    if (options.ohovMpOfflineSource === null) {
      playLogRuntime?.recordOHovExecute({
        ok: true,
        executionSource: "mp",
        beforeSnapshot: event.beforeSnapshot,
        snapshot: event.afterSnapshot,
      });
      return;
    }
    playLogRuntime?.recordOHovAutoExecute({
      source: options.ohovMpOfflineSource,
      executionSource: "mp",
      beforeSnapshot: event.beforeSnapshot,
      snapshot: event.afterSnapshot,
    });
  };

  const advanceForegroundFrame = (): ForegroundPresentationFrame => {
    autoResetTargetPresentationGate.beginFrame();

    const frameBaselineAtMs = nextRuntimeFrameBaselineAtMs;
    const rawDtSeconds =
      frameBaselineAtMs === null
        ? runtime.engine.getDeltaTime() / 1000
        : Math.max(0, (performance.now() - frameBaselineAtMs) / 1000);
    nextRuntimeFrameBaselineAtMs = null;
    const adminSpeedFrame =
      adminGameSpeedPolicyController.resolveFrame(rawDtSeconds);
    const sameSessionCatchUp = pendingSameSessionCatchUps[0] ?? null;
    const continuousElapsedSeconds =
      adminSpeedFrame.continuousElapsedSeconds +
      (sameSessionCatchUp?.continuousElapsedSeconds ?? 0);
    const timerElapsedSeconds =
      adminSpeedFrame.timerElapsedSeconds +
      (sameSessionCatchUp?.timerElapsedSeconds ?? 0);
    const amplifierProgressionElapsedSeconds =
      adminSpeedFrame.amplifierProgressionElapsedSeconds +
      (sameSessionCatchUp?.amplifierProgressionElapsedSeconds ?? 0);
    const visualElapsedSeconds =
      adminSpeedFrame.visualElapsedSeconds +
      (sameSessionCatchUp?.visualElapsedSeconds ?? 0);
    const shouldAdvanceGameplay =
      !isOfflineRewardCommitInProgress &&
      !(__HOVER_IDLE_ADMIN_PANEL_BUILD__ && isAdminCheckpointRestoring) &&
      continuousElapsedSeconds > 0;
    let didCompleteRecentHovRateSegment = false;
    let processedSeconds = 0;
    if (shouldAdvanceGameplay) {
      const advanceResult = model.advanceOnlineGameplay({
        continuousElapsedMs: continuousElapsedSeconds * 1000,
        timerElapsedMs: timerElapsedSeconds * 1000,
        amplifierProgressionElapsedMs:
          amplifierProgressionElapsedSeconds * 1000,
        resetAutomationEventBudget: Math.max(
          adminSpeedFrame.automation.resetEvents.budget,
          sameSessionCatchUp?.automation.resetEvents.budget ?? 0,
        ),
        hguPurchaseEventBudget: Math.max(
          adminSpeedFrame.automation.hguPurchaseEvents.budget,
          sameSessionCatchUp?.automation.hguPurchaseEvents.budget ?? 0,
        ),
      });
      processedSeconds = advanceResult.continuousElapsedMs / 1000;
      const wasSameSessionIntervalOpen = absenceLifecycle.hasOpenInterval;
      if (wasSameSessionIntervalOpen) {
        absenceLifecycle.recordGameplayProcessed(
          adminSpeedFrame.safeRawElapsedSeconds * 1000,
        );
      }
      if (sameSessionCatchUp !== null) {
        pendingSameSessionCatchUps.shift();
        pendingSameSessionCatchUpRawElapsedMs = Math.max(
          0,
          pendingSameSessionCatchUpRawElapsedMs -
            sameSessionCatchUp.rawElapsedSeconds * 1000,
        );
      }
      const liveWallElapsedSeconds = wasSameSessionIntervalOpen
        ? adminSpeedFrame.safeRawElapsedSeconds
        : adminSpeedFrame.rawElapsedSeconds;
      const processedWallElapsedSeconds =
        liveWallElapsedSeconds + (sameSessionCatchUp?.rawElapsedSeconds ?? 0);
      playLogRuntime?.advanceTime({
        wallDeltaMs: processedWallElapsedSeconds * 1000,
        gameDeltaMs: advanceResult.continuousElapsedMs,
        speedMultiplier:
          processedWallElapsedSeconds > 0
            ? advanceResult.continuousElapsedMs /
              (processedWallElapsedSeconds * 1000)
            : adminSpeedFrame.policy.continuous.effectiveSpeedMultiplier,
      });
      recentHovCoreRateClock.advanceByGameDtSeconds(processedSeconds);
      if (processedSeconds > 0) {
        const tick = fairyModel.update(visualElapsedSeconds);
        for (let i = 0; i < tick.popped.length; i += 1) {
          model.addFai(FAIRY_POP_FAI_REWARD);
        }
      }
      const autoResetReadoutSnapshots =
        model.consumeLastAutoResetExecuteBeforeSnapshots();
      const autoResetEvents = model.consumeLastAutoResetExecutionEvents();
      const hguAutomationEvents =
        model.consumeLastHovGainUpgradeMpAutomationEvents();
      const snapshot = model.getSnapshot();
      const autoResetExecution = prepareAutoResetTargetPresentation({
        recoverLiveHovTargetInput: true,
        authoritativeSnapshot: snapshot,
        finalGameplayElapsedSeconds: processedSeconds,
        events: autoResetEvents,
        readoutSnapshots: autoResetReadoutSnapshots,
      });
      didCompleteRecentHovRateSegment =
        autoResetExecution.didCompleteRecentHovRateSegment;
      handleGameplayAutomationSideEffects(
        autoResetEvents,
        hguAutomationEvents,
        snapshot,
        {
          ohovAutoSource: "online-update",
          ohovMpOfflineSource: null,
          deferViewRefresh: true,
        },
      );
    }

    // Achievement rewards can mutate Exploration-visible state (Tim
    // acceleration unlock and MP capacity). Settle them before taking the
    // presentation snapshot so every area and control reads one post-update
    // state whenever this authoritative frame is selected for display.
    evaluateAchievements();
    const snapshot = model.getSnapshot();
    const shouldSampleRecentHovRate =
      shouldAdvanceGameplay && !didCompleteRecentHovRateSegment;
    recentHovRateTracker.advance(
      shouldSampleRecentHovRate ? processedSeconds : 0,
      shouldSampleRecentHovRate ? model.getHovEarnedThisTick() : hugeZero(),
    );
    const frameObservedAtMs = recentHovCoreRateClock.nowMs();
    recentHovCoreRateTracker.observe(frameObservedAtMs, snapshot.oHov.hovCore);
    syncUpgradeMpAutomations(snapshot);
    if (
      __HOVER_IDLE_ADMIN_PANEL_BUILD__ &&
      ADMIN_PANEL_AVAILABLE &&
      !isAdminCheckpointRestoring
    ) {
      adminCheckpoints?.observeProgress(snapshot);
    }
    playLogRuntime?.recordDueSample(snapshot);
    playLogRuntime?.recordDueResetPreviewProbe(snapshot);
    return {
      snapshot,
      recentHovCoreRatePerHour:
        recentHovCoreRateTracker.getRate(frameObservedAtMs),
      isStableFrameCommit: shouldAdvanceGameplay,
    };
  };

  const presentForegroundFrame = (frame: ForegroundPresentationFrame): void => {
    const { snapshot } = frame;
    updateHovView(snapshot, frame.recentHovCoreRatePerHour, {
      isStableFrameCommit: frame.isStableFrameCommit,
      suppressTargetStructureCommit: isOfflineRewardCommitInProgress,
    });
    if (
      outsideResetBurstScheduler.evaluate({
        serial: snapshot.outsideResetSerial,
        mode: resolveHoverIdleMotionPolicy(model.getSettings())
          .outsideResetBurstAnimation,
        isHovTargetScreenActive: isHovTargetActive,
      }) === "play"
    ) {
      outsideResetBurstController.playBurst();
    }
    timView.update(snapshot, recentHovRateTracker.getRate());
    mpmView.update(snapshot);
    wallView.refresh(snapshot);
    if (activeHovTabId === "hov-exploration") {
      explorationView.update(toExplorationScreenSnapshot(snapshot));
    }
    hovView.setWallTabDot(shouldShowWallTabDot(snapshot));
    refreshHelpNotifications();
    fairyView.update(fairyModel.getSnapshot());
    if (import.meta.env.VITE_ENABLE_E2E_TEST_HOOKS === "1") {
      markE2EForegroundPresentation();
    }
    runtime.scene.render();
  };

  const foregroundFrameDriver = createForegroundFrameDriver({
    lowPowerMode: model.getSettings().lowPowerMode,
    advanceFrame: advanceForegroundFrame,
    presentFrame: presentForegroundFrame,
  });
  setForegroundStandbyMode = (enabled): void => {
    foregroundFrameDriver.setStandbyMode(enabled);
  };
  if (isStandbyMode) {
    foregroundFrameDriver.setStandbyMode(true);
  }

  const runForegroundFrame = (): void => {
    if (isDisposed || isOfflineRewardCommitInProgress) {
      return;
    }
    if (isCommittedResultAwaitingFirstPaint) {
      runtime.scene.render();
      return;
    }
    foregroundFrameDriver.runFrame(performance.now());
  };

  window.addEventListener("resize", resize);
  resize();
  const initialSnapshot = model.getSnapshot();
  const initialViewObservedAtMs = recentHovCoreRateClock.nowMs();
  updateHovView(
    initialSnapshot,
    recentHovCoreRateTracker.getRate(initialViewObservedAtMs),
  );
  timView.update(initialSnapshot, recentHovRateTracker.getRate());
  mpmView.update(initialSnapshot);
  wallView.refresh(initialSnapshot);
  hovView.setWallTabDot(shouldShowWallTabDot(initialSnapshot));
  syncUpgradeMpAutomations(initialSnapshot);
  if (initialEntitlementSync.didChange) {
    autosave.saveAfterImportantEvent();
  }
  const runGameplayRuntime = (): void => {
    if (!isRunning || isDisposed || isOfflineRewardCommitInProgress) return;
    runtime.engine.runRenderLoop(runForegroundFrame);
  };
  const startGameplayRuntime = (): void => {
    if (isRunning || isDisposed) return;
    isRunning = true;
    nextRuntimeFrameBaselineAtMs = performance.now();
    runGameplayRuntime();
  };
  let offlineResultModal: OfflineRewardResultModal | null = null;
  const pendingOfflineResultModalModels: OfflineRewardResultModalModel[] = [];
  const dismissOfflineResultModal = (): void => {
    const current = offlineResultModal;
    if (current === null) return;
    offlineResultModal = null;
    current.dispose();
    const next = pendingOfflineResultModalModels.shift();
    if (!isDisposed && next !== undefined) {
      showOfflineResultModal(next);
      return;
    }
    pointerModel.republish();
  };
  const showOfflineResultModal = (
    resultModel: OfflineRewardResultModalModel,
  ): void => {
    if (offlineResultModal !== null) {
      pendingOfflineResultModalModels.push(resultModel);
      return;
    }
    offlineResultModal = createOfflineRewardResultModal({
      isTouchPrimary,
      showHoverAnchorNotice:
        isTouchPrimary && model.getSettings().firstAccessTutorialCompleted,
      model: resultModel,
      messages: getMessages().offline.modal,
      formatDuration,
      parent: root,
      onDismiss: dismissOfflineResultModal,
    });
    resetHoverIdleInteractionContext();
    pointerModel.republish();
  };
  const offlineRewardPresentationDispatcher =
    createOfflineRewardPresentationDispatcher<OfflineRewardResultModalModel>({
      showToast: (resultModel) => {
        hovView.showNotice(formatOfflineRewardToast(resultModel), {
          dismissAfterHoverMs: 100,
          groupKey: "offline-reward-result",
          maxVisibleCount: 1,
        });
      },
      showModal: showOfflineResultModal,
    });
  const presentCommittedOfflineReward = (
    rewardOutcome: Extract<
      ProductionOfflineRewardOutcome,
      { readonly kind: "committed" }
    >,
  ) =>
    offlineRewardPresentationDispatcher.dispatch({
      rewardId: rewardOutcome.result.rewardId,
      appliedGameElapsedMs: rewardOutcome.result.receipt.gameElapsedMs,
      createModel: () => createOfflineRewardResultModel(rewardOutcome.result),
    });

  if (startupOfflineReward?.kind === "committed") {
    runtime.scene.render();
    await options.onStartupGameSurfaceReady?.();
    startGameplayRuntime();
    try {
      const presentation = presentCommittedOfflineReward(startupOfflineReward);
      isCommittedResultAwaitingFirstPaint =
        presentation.status === "dispatched" &&
        presentation.presentation === "modal";
      if (
        presentation.status === "dispatched" &&
        presentation.presentation !== "none"
      ) {
        markStartupTrace("offline-result-ui-created");
        await waitForPaint();
        markStartupTrace("game-surface-first-paint");
        markStartupTrace("offline-result-first-paint");
      }
    } finally {
      isCommittedResultAwaitingFirstPaint = false;
    }
  }

  const applyCommittedOfflineSaveToLiveRuntime = (
    saveData: HoverIdleSaveData,
  ): void => {
    achievements.restoreState(achievementsStateFromHoverIdleSaveData(saveData));
    model.restoreState(modelStateFromHoverIdleSaveData(saveData));
    hovSkillModel.restoreState(hovSkillStateFromHoverIdleSaveData(saveData));
    timSkillModel.restoreState(timSkillStateFromHoverIdleSaveData(saveData));
    records.restoreState(recordsStateFromHoverIdleSaveData(saveData));
    syncAchievementPersistentEffects();
    syncTimHovActivationUnlock();
    syncHovSkillEffects();
    for (const id of timSkillModel.getSnapshot().acquired) {
      applyTimSkillEffect(id);
    }
    firstPlayedAt = saveData.timestamps.firstPlayedAt ?? firstPlayedAt;
    lastSavedAt = saveData.timestamps.savedAt;
    offlineRewardMetadata = cloneOfflineRewardSaveMetadata(
      saveData.offlineReward,
    );
    recentHovRateTracker.reset();
    recentHovCoreRateTracker.reset(
      model.getSnapshot().oHov.hovCore,
      recentHovCoreRateClock.nowMs(),
    );
    fairyModel.reset();
    syncReduceMotionState();
    foregroundFrameDriver.setLowPowerMode(model.getSettings().lowPowerMode);
    settingsPanel?.update(model.getSettings(), getSettingsPanelRuntime());
    refreshView();
    refreshHelpNotifications();
    syncPaymentPanels();
    achievementsView.update(achievements.getSnapshot(), {
      showUnseenDots: shouldShowAchievementUnseenDots(),
    });
    refreshAchievementsBadge();
    recordsView.update(records.getSnapshot());
  };

  const beginSamePageBackgroundInterval = (): void => {
    resetHoverIdleInteractionContext();
    if (absenceLifecycle.handleHidden(nowMs())) {
      nextRuntimeFrameBaselineAtMs = performance.now();
    }
  };

  const checkpointPotentialPageSuspend = (): void => {
    resetHoverIdleInteractionContext();
    if (absenceLifecycle.handleCheckpoint(nowMs())) {
      nextRuntimeFrameBaselineAtMs = performance.now();
    }
  };

  const resumeSamePageBackgroundInterval = (): void => {
    if (absenceLifecycle.handleResume(nowMs())) {
      nextRuntimeFrameBaselineAtMs = performance.now();
    }
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      beginSamePageBackgroundInterval();
      return;
    }
    resumeSamePageBackgroundInterval();
    requestRestoreHoverAnchor("visibility-visible");
  };
  document.addEventListener("visibilitychange", handleVisibilityChange);

  const handlePageHide = (): void => {
    checkpointPotentialPageSuspend();
  };
  window.addEventListener("pagehide", handlePageHide);

  const handlePageShow = (): void => {
    if (document.visibilityState !== "hidden") {
      resumeSamePageBackgroundInterval();
    }
    requestRestoreHoverAnchor("pageshow");
  };
  window.addEventListener("pageshow", handlePageShow);

  const handleFreeze = (): void => {
    checkpointPotentialPageSuspend();
  };
  document.addEventListener("freeze", handleFreeze);

  const handleDocumentResume = (): void => {
    if (document.visibilityState !== "hidden") {
      resumeSamePageBackgroundInterval();
    }
  };
  document.addEventListener("resume", handleDocumentResume);

  const simulateOfflineMinutes = async (minutes: number) => {
    if (!Number.isFinite(minutes) || minutes < 0) {
      throw new Error("minutes must be a finite non-negative number");
    }
    const simulatedNowMs = nowMs();
    const requestedElapsedMs = minutes * 60_000;
    const rewardBaselineAt = Math.max(0, simulatedNowMs - requestedElapsedMs);
    const injectedRealElapsedMs = simulatedNowMs - rewardBaselineAt;
    isOfflineRewardCommitInProgress = true;
    isOrdinarySaveSuspendedForRewardCommit = true;
    try {
      await saveQueue;
      lastSavedAt = simulatedNowMs;
      const startSaveSnapshot = createHoverIdleSaveData({
        state: model.exportState(),
        hovSkills: hovSkillModel.exportState(),
        timSkills: timSkillModel.exportState(),
        achievements: achievements.exportState(),
        records: records.exportState(),
        timestamps: {
          savedAt: simulatedNowMs,
          firstPlayedAt,
        },
        offlineReward: advanceSaveRevision(rewardBaselineAt),
      });
      await storage.save(startSaveSnapshot);
      const outcome = await calculateAndCommitProductionOfflineRewardSave({
        storage,
        startSave: startSaveSnapshot,
        committedAt: simulatedNowMs,
        ownerEntitlementSnapshot: currentOwnerEntitlementSnapshot,
        adminTimeScale: adminGameSpeedPolicyController.get(),
        systemTimeScale: 1,
      });
      if (outcome.kind === "not-needed") {
        return {
          injectedRealElapsedMs,
          uncappedGameElapsedMs: 0,
          rewardGameElapsedMs: 0,
          timGained: "0",
          rewardVersion: OFFLINE_REWARD_FORMULA_VERSION,
          capApplied: false,
          presentation: "none" as const,
        };
      }
      if (
        outcome.kind === "commit-unavailable" ||
        outcome.kind === "invalid-result"
      ) {
        throw new Error(formatUnavailableOfflineRewardSummary());
      }
      if (outcome.kind === "commit-rejected") {
        throw new Error(formatRejectedOfflineRewardSummary());
      }

      applyCommittedOfflineSaveToLiveRuntime(outcome.saveData);
      if (!isStandbyMode) {
        runtime.scene.render();
      }
      recordOfflineReward(outcome, "admin-offline-reward");
      isOfflineRewardCommitInProgress = false;
      isOrdinarySaveSuspendedForRewardCommit = false;
      runGameplayRuntime();
      const presentation = presentCommittedOfflineReward(outcome);
      return {
        injectedRealElapsedMs,
        uncappedGameElapsedMs: outcome.elapsed.uncappedGameElapsedMs,
        rewardGameElapsedMs: outcome.elapsed.rewardElapsedMs,
        timGained: outcome.result.timDelta,
        rewardVersion: outcome.input.rewardFormulaVersion,
        capApplied: outcome.elapsed.wasCapped,
        presentation:
          presentation.status === "duplicate"
            ? ("none" as const)
            : presentation.presentation,
      };
    } finally {
      isOfflineRewardCommitInProgress = false;
      isOrdinarySaveSuspendedForRewardCommit = false;
      runGameplayRuntime();
    }
  };

  if (session.loadFailureMessage !== null) {
    hovView.showNotice(session.loadFailureMessage, {
      dismissAfterHoverMs: 100,
    });
  }
  if (
    startupOfflineReward?.kind === "commit-unavailable" ||
    startupOfflineReward?.kind === "invalid-result"
  ) {
    hovView.showNotice(formatUnavailableOfflineRewardSummary(), {
      dismissAfterHoverMs: 100,
    });
  } else if (startupOfflineReward?.kind === "commit-rejected") {
    hovView.showNotice(formatRejectedOfflineRewardSummary(), {
      dismissAfterHoverMs: 100,
    });
  }
  if (ADMIN_PANEL_AVAILABLE) {
    mountAdminPanel();
  }
  const refreshEntitlementsFromRemote = async (): Promise<void> => {
    if (isDisposed) return;
    bootMark("deferred entitlement load start");
    let remote: Awaited<ReturnType<typeof loadEntitlementSnapshot>>;
    if (startupOwnerEntitlements !== null) {
      remote = startupOwnerEntitlements;
      startupOwnerEntitlements = null;
    } else {
      try {
        remote = await loadEntitlementSnapshot(
          playerOwner.ownerId,
          entitlementRepository,
        );
      } catch (error) {
        console.warn("Failed to load remote entitlements:", error);
        return;
      }
    }
    bootMark("deferred entitlement load resolved");
    if (isDisposed) return;
    currentOwnerEntitlementSnapshot = remote.snapshot;
    model.setOwnerEntitlementSnapshot(remote.snapshot);
    const merged = mergeBundleEntitlements(
      model.getPaymentState().entitlements,
      remote.bundleKeys,
    );
    const sync = syncEntitlementsAndApplyEffects(model, merged);
    refreshView();
    syncPaymentPanels();
    if (sync.didChange) {
      autosave.saveAfterImportantEvent();
    }
    bootMark("deferred entitlement apply end");
  };
  // Fire after the synchronous return chain completes so main.ts can run
  // app.start() and dismissBootLoading() first. Ignore the timer id: the
  // load is also guarded by isDisposed to avoid late writes after teardown.
  deferredEntitlementTimerId = window.setTimeout(() => {
    deferredEntitlementTimerId = null;
    void refreshEntitlementsFromRemote();
  }, 0);

  return {
    start(): void {
      startGameplayRuntime();
    },

    startPlayLogSession,

    stopPlayLogSession,

    exportState(): HoverIdleModelState {
      return model.exportState();
    },

    exportHovSkillState(): HovSkillState {
      return hovSkillModel.exportState();
    },

    exportTimSkillState(): TimSkillState {
      return timSkillModel.exportState();
    },

    exportAchievementsState(): AchievementsState {
      return achievements.exportState();
    },

    exportRecordsState(): RecordsState {
      return records.exportState();
    },

    exportPlayLogJsonLines(): string {
      return exportPlayLogJsonLines();
    },

    clearPlayLog,

    getPlayLogStatus,

    dispose(): void {
      if (isDisposed) {
        return;
      }

      isDisposed = true;
      adminCheckpoints?.dispose();
      playLogRuntime?.stopSession({ endedAtMs: nowMs() });
      if (deferredEntitlementTimerId !== null) {
        window.clearTimeout(deferredEntitlementTimerId);
        deferredEntitlementTimerId = null;
      }
      isOfflineRewardCommitInProgress = false;
      autosave.dispose();
      void saveNow();
      amplifierUpgradeAutomation?.dispose();
      amplifierUpgradeAutomation = null;
      runtime.engine.stopRenderLoop(runForegroundFrame);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("freeze", handleFreeze);
      document.removeEventListener("resume", handleDocumentResume);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      absenceLifecycle.dispose();
      pendingOfflineResultModalModels.length = 0;
      dismissOfflineResultModal();
      hoverAnchorRestoreController.dispose();
      for (const cleanup of navCleanups) cleanup();
      outsideResetBurstController.dispose();
      hovView.dispose();
      timView.dispose();
      fairyView.dispose();
      pointerInputController.dispose();
      hoverAnchorPersistence.dispose();
      pointerView.dispose();
      pointerModel.dispose();
      options.canvas.hidden = false;
      root.remove();
      sceneResources.dispose();
      runtime.scene.dispose();
      runtime.engine.dispose();
    },
  };
}

function reasonFromUnknown(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
